import { AppIcon, AppText } from "@toothfairy/shared-ui";
import AppAssets from "@toothfairy/shared-ui/AppAssets";
import AppBaseScreen from "@toothfairy/shared-ui/AppBaseScreen";
import AppChoice from "@toothfairy/shared-ui/AppChoice";
import AppPlaceholderView from "@toothfairy/shared-ui/AppPlaceholderView";
import AppQuantumChat from "@toothfairy/shared-ui/AppQuantumChat";
import AppStateManager from "@toothfairy/shared-ui/AppStateManager";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import AppToast from "@toothfairy/shared-ui/AppToast";
import AppUser from "@toothfairy/shared-ui/AppUser";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Platform, View, useWindowDimensions } from "react-native";
import { Popable } from "react-native-popable";
import { isConnectedToCloud, isElectronRunning } from "../../../App";
import AppJoyRide from "../../Components/Joyride";
import LoadingPlaceholder from "../../Components/LoadingPlaceholder";
import EditorWrapper from "../../Components/EditorWrapper";
import PlannerStepsViewer from "../../Components/PlannerStepsViewer";
import ToothFairyButton from "../../Components/ToothFairyComponents/ToothFairyButton";
import ToothFairyClickableOption from "../../Components/ToothFairyComponents/ToothFairyClickableOption";
import ToothFairyModal from "../../Components/ToothFairyComponents/ToothFairyModal";
import ToothFairyResponsiveOptionsModal from "../../Components/ToothFairyComponents/ToothFairyResponsiveOptionsModal";
import ToothFairySupportButton from "../../Components/ToothFairyComponents/ToothFairySupportButton";
import AppTreeView from "../../Components/TreeView";
import useUserHelperPreferences from "../../Hooks/useUserHelperPreferences";
import useOnboardingTour, { TOUR_FLAGS } from "../../Hooks/useOnboardingTour";
import useWorkspaces from "../../Hooks/useWorkspaces";
import { useHistory } from "../../Router";
import Utility from "../../Utility";
import {
  isLargeScreen,
  isMobileScreen,
  minAppWidth,
} from "../MainViewContainer";
import ChatCreationForm from "./Components/ChatCreationForm";
import ChatWebInput from "./Components/ChatWebInput";
import ReferencesView from "./Components/ReferencesView";
import {
  useModels,
  useExternalModels,
  useRegionalModelIds,
} from "../Common/baseModels";
import trainingLogHelper from "../../API/TrainingLog/trainingLogHelper";
import envConfig from "../../../envConfig";
import AppDropDown from "@toothfairy/shared-ui/AppDropDown";
import { HostingType } from "../../models";
import ModelPricingInfo from "../../Components/ModelPricingInfo";
import FineTunedModelStatusChip from "../../Components/ToothFairyComponents/FineTunedModelStatusChip";
import FineTunedModelPricingInfo from "../../Components/ToothFairyComponents/FineTunedModelPricingInfo";
import {
  getToggleableToolsForMode,
  getAgenticCycleState,
  AGENTIC_CYCLE_CONFIG,
} from "../../Utils/toggleableToolsConfig";
import { shouldShowInChat } from "../../Utils/plannerStepUtils";
import { LLM_VOICE_MODELS } from "../Common/voiceModels";
import UoIUsageModal from "../../Components/UoIUsageModal/UoIUsageModal";

const ChatsScreenView = ({
  isHomeMode = false,
  homeAgentId = null,
  builderMode = false,
  liveSessionId = null,
  onVoiceSessionEnded = () => {},
  handleClickOnNode,
  SaveChat,
  selectedNode,
  deleteNode,
  showModal,
  setShowModal,
  setDialogActionOnTree,
  treeViewActionContext,
  isTreeDataLoading,
  selectedTopics,
  onSelectedTopicsChange,
  topics,
  onDrop,
  _coreTreeViewData,
  selectedNodeToDelete,
  creationofNewDocumentInProgress,
  isCollapsed,
  setIsCollapsed,
  onMove,
  chatId,
  primaryAgent,
  resolvedAgentFunctions = [],
  agents = [],
  onAgentSelected = () => {},
  onSendWithAgent = () => {},
  isLoading,
  messages,
  pendingUserMessages = [],
  setMessages,
  onLoadEarlier = () => {},
  chatObject,
  isAIStreaming,
  isAIReplyingOtherUsers,
  waitingForFirstResponse = false,
  waitingForFirstChunk = false,
  isAwaitingUserInput = false,
  onCancelGeneration = () => {},
  onSendEvent = () => {},
  handleShowSourcesDetails = () => {},
  handleParentActionClick = () => {},
  onToggleFavorite = () => {},
  handleFeedback = () => {},
  handleClickOnWidgetChatsHistory = () => {},
  listenToAudio = () => {},
  inputText,
  setInputText,
  allMessagesLoaded,
  subscriptionObj,
  streamingMessageID,
  documents,
  documentId,
  setTreeViewActionContext,
  S3downloadUrlRequest,
  workspaceid,
  errorWithSubscription,
  handleCopy = () => {},
  showSourcesModal,
  setShowSourcesModal,
  selectedSources,
  isMoreToLoad,
  creationOfNewChatInProgress,
  allowStop = false,
  setIsChatHistoryLoaded,
  isLastMessageEmpty,
  showWidgetChatsHistory,
  setShowWidgetChatsHistory,
  canInteractWithChat,
  onImageUploaded,
  imageUrl,
  setImageUrl,
  onAudioUploaded,
  audioUrl,
  setAudioUrl,
  onFileUploaded,
  fileUrl,
  setFileUrl,
  onVideoUploaded,
  videoUrl,
  setVideoUrl,
  onImageSelected = () => {},
  selectedImageKey,
  onFilesUploaded = () => {},
  fileUrls,
  setFileUrls,
  forceAgentsName,
  activeStep,
  activeSteps,
  planningExecution,
  totalSteps,
  setSelectedNode,
  isSpeechGettingProcessed,
  updateTextForSpeech = () => {},
  stopAudio = () => {},
  isPlaying,
  lastUpdateMessageTime,
  agentHosting,
  planArtifacts,
  stepsStatusFromArtifacts = [],
  cursor,
  isFeedbackOnPlanAllowed,
  isAgenticRAGAcceptingFeedback,
  isSocketConnected,
  updatePlanApprovalState,
  imageUrls,
  audioUrls,
  hasAccessToChat,
  // EditorWrapper props for canvas collaboration
  canvasId,
  value,
  editor,
  isInactive,
  updateAPIError,
  updateAPIInProgress,
  createAPIInProgress,
  saveDocument,
  handleSocketConnectionChange,
  handleEditorError,
  setValue,
  setEditor,
  editorIsLoading,
  isReadyForRTC,
  isDocumentValid,
  wordsCount,
  charactersCount,
  isSaved,
  setLastCancellation,
  setIsSaveRequested,
  fixedDocumentId,
  handleOnRecorderClick = () => {},
  handleCanvasCreation = () => {},
  onSelectionQuantumEditor = () => {},
  // Plan mode props
  planMode = false,
  planFile = null,
  planStatus = null,
  handlePlanModeToggle = () => {},
  handleShowPlan = () => {},
  // Fork conversation
  handleForkConversation = () => {},
  // Model selection
  onBaseModelChange = () => {},
  isModelChangeInProgress = false,
  canChangeModel = false,
  // Tool toggle
  onToolToggle = () => {},
  toolToggleInProgress = null,
  canToggleTools = false,
  // Hand off toggle
  onHandOffToggle = () => {},
  // Folder binding props
  onFolderBound = () => {},
  onFolderUnbound = () => {},
  recentFolders = [],
  onUpdateRecentFolders = () => {},
  // Mark for training
  onMarkForTraining = () => {},
  // UoI usage visibility
  canViewUoIUsage = false,
}) => {
  const { baseModels: _baseModels } = useModels();
  const {
    externalModels: _externalModels,
    externalDeprecatedModels: _externalDeprecatedModels,
  } = useExternalModels();
  const _regionalModelIds = useRegionalModelIds();
  const _reasoningModels = _baseModels
    ?.filter((u) => u?.reasoner)
    ?.map((u) => u?.id);
  const formRef = useRef();
  const { state, dispatch } = AppStateManager.useAppStateManager();
  const [agentImage, setAgentImage] = useState(null);
  const history = useHistory();
  const { currentTheme } = AppTheme.useTheme();
  const AppToasty = AppToast.useToast();
  const { t } = useTranslation();
  const [isModalOptionsOpen, setIsModalOptionsOpen] = useState(false);
  const [isTopicModalOpen, setIsTopicModalOpen] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [isPlannerModalShown, setShowPlannerModal] = useState(false);
  const [isArtifactsModalShown, setShowArtifactsModal] = useState(false);
  const [isCitationModalShown, setShowCitationModal] = useState(false);
  const [isModelSelectionModalShown, setShowModelSelectionModal] =
    useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [selectedModel, setSelectedModel] = useState(null);
  const [selectedReasoningEffort, setSelectedReasoningEffort] = useState(null);
  const [selectedRegionalEndpoint, setSelectedRegionalEndpoint] =
    useState(null);
  // Fine-tuned models of this workspace (completed runs) for the quick model
  // selection — mirrors the QA settings base-model dropdown behaviour.
  const [finetunedModels, setFinetunedModels] = useState([]);
  const [selectedCitation, setSelectedCitation] = useState(null);
  const [isToolsModalOpen, setIsToolsModalOpen] = useState(false);
  const [isTopicsModalOpen, setIsTopicsModalOpen] = useState(false);
  const [isOtherToolsModalOpen, setIsOtherToolsModalOpen] = useState(false);
  const [isQueueModalOpen, setIsQueueModalOpen] = useState(false);
  const [isCollapsedToolsModalOpen, setIsCollapsedToolsModalOpen] =
    useState(false);
  const [showUoIUsageModal, setShowUoIUsageModal] = useState(false);
  // Track whether the planner steps panel is expanded (default: true)
  const [plannerStepsExpanded, setPlannerStepsExpanded] = useState(true);
  // Track whether the canvas panel is visible (default: open when a canvas exists)
  const [isCanvasOpen, setIsCanvasOpen] = useState(true);

  // Reset canvas visibility whenever the active canvas changes (chat switch / new canvas)
  useEffect(() => {
    setIsCanvasOpen(true);
  }, [documentId]);

  // Joyride steps defined inside component to use t()
  const steps_first_interaction = useMemo(
    () => [
      {
        disableBeacon: true,
        target: ".upload_docs",
        content: t("chats.joyride.knowledgeHubStep"),
        placement: "right",
      },
    ],
    [t]
  );

  const steps_second_interaction = useMemo(
    () => [
      {
        disableBeacon: true,
        target: ".start_chat",
        content: t("chats.joyride.startChatStep"),
        placement: "left",
      },
    ],
    [t]
  );
  const { user } = AppUser.useUser();
  const { updateUserHelperPreferences } = useUserHelperPreferences();
  const {
    needsTour,
    completeTourIfEligible,
    isPlatformSupported,
    useTourReady,
  } = useOnboardingTour();
  const phaseTwo = user?.userHelperPreferences?.knowledgeHubVisited === true;
  const chatTourReady = useTourReady(
    phaseTwo ? [".start_chat"] : [".upload_docs"]
  );
  const {
    getActiveWorkspace,
    loadChats,
    mapModeWithLabel,
    isUserContentAdmin,
    isUserAIEngineer,
    getAgent,
    getWorkspaceEntities,
    getFinetunedModels,
  } = useWorkspaces();

  const agentGroupTopicById = useMemo(() => {
    const topics = (getWorkspaceEntities?.("topic") || []).filter(
      (e) => !e?._deleted
    );
    const map = {};
    topics.forEach((topic) => {
      map[topic.id] = topic;
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getWorkspaceEntities?.("topic")?.length]);
  const { height } = useWindowDimensions();

  // Whether the current agent is tf_main (Fairy AI) — used for UI overrides
  // This reflects the actual agent, not just the route, so it updates on agent switch
  const isTfMainAgent =
    primaryAgent?.id?.startsWith("tf_main") ||
    (isHomeMode && !primaryAgent?.id);

  // Feature flag for PlannerStepsViewer - must be defined before use in JSX
  const showPlannerSteps =
    primaryAgent?.mode === "planner" && messages?.length > 0 && !documentId;

  const _selectedNodeType = selectedNode?.data?.droppable ? "node" : "chat";
  const fetchAgentImage = async (workspaceid) => {
    const _url = await S3downloadUrlRequest({
      filename: `imported-image/${workspaceid}/${primaryAgent?.icon}`,
      context: "pdf",
      workspaceid,
    });
    setAgentImage(_url?.url);
  };
  const fetchStepArtifact = async (key) => {
    const _url = await S3downloadUrlRequest({
      filename: key,
      context: "pdf",
      workspaceid,
    });
    if (isElectronRunning())
      window.electronAPI.downloadFileDl(_url?.url, "_blank");
    else window.open(_url?.url, "_blank");
  };

  useEffect(() => {
    if (primaryAgent?.icon && workspaceid) fetchAgentImage(workspaceid);
    else {
      setAgentImage(null);
    }
  }, [primaryAgent?.icon, workspaceid]);

  useEffect(() => {
    if (!showModal) {
      formRef?.current?.resetForm();
      setShowModal(false);
      setTreeViewActionContext("");
    }
  }, [showModal]);

  // EditorWrapper styles for canvas collaboration
  const _editorStyles = useMemo(() => {
    return {
      wrapperStyle: {
        marginBottom: 10,
        flex: 1,
        padding: 10,
        paddingTop: 15,
        marginLeft: 20,
        borderWidth: 1,
        borderRadius: 10,
        borderColor: currentTheme?.theme?.border_color,
        maxHeight: height - 100,
      },
      style: {
        overflowY: Platform.OS === "web" ? "auto" : "scroll",
        flex: 1,
        zIndex: -1,
        fontFamily: "normalFont",
      },
      titleStyle: {
        fontSize: currentTheme?.theme?.base_font_size,
      },
    };
  }, [currentTheme, height]);

  const getAgentObject = (agentId) => {
    return state?.agents?.find((u) => u.id == agentId);
  };
  function sanitizeString(inputString) {
    // Step 1: Remove ```mermaid ``` clauses
    let sanitizedString = inputString;

    // Step 2: Replace markdown image tags with ![image]
    const imageRegex = /!\[tf_image\]\([^)]+\)/g;
    sanitizedString = sanitizedString.replace(imageRegex, "![image]");

    return sanitizedString;
  }
  const createMessagesExportFile = () => {
    // using getSiteObject()?.siteTree generate a txt file and allow the user to download it
    const element = document.createElement("a");
    const file = new Blob(
      [
        JSON.stringify(
          messages?.map((u) => {
            return {
              id: u?._id,
              user: u?.user,
              text: sanitizeString(u?.text),
              createdAt: u?.createdAt,
            };
          }),
          undefined,
          4
        ),
      ],
      {
        type: "text/plain;charset=utf-8",
      }
    );
    element.href = URL.createObjectURL(file);
    element.download = `${chatId}-messages-export.txt`;
    document.body.appendChild(element);
    element.click();
  };

  const exportPlanToClipboard = () => {
    try {
      const metadata =
        typeof chatObject?.metadata === "string"
          ? JSON.parse(chatObject.metadata)
          : chatObject?.metadata;

      const plan =
        typeof metadata?.plan === "string"
          ? JSON.parse(metadata.plan)
          : metadata?.plan;

      if (!plan || !Array.isArray(plan) || plan.length === 0) {
        AppToasty.show(t("chats.toasts.noPlanFound"), { type: "warning" });
        return;
      }

      const exportData = { plan };
      const jsonString = JSON.stringify(exportData, null, 2);

      navigator.clipboard
        .writeText(jsonString)
        .then(() => {
          AppToasty.show(t("chats.toasts.planCopied"), { type: "success" });
        })
        .catch(() => {
          AppToasty.show(t("chats.toasts.failedToCopyPlan"), {
            type: "danger",
          });
        });
    } catch (e) {
      AppToasty.show(t("chats.toasts.failedToParsePlan"), { type: "danger" });
    }
  };

  const getAgentDescription = (agent) => {
    return `${agent?.label} - ${mapModeWithLabel(agent?.mode)}`;
  };

  const MAX_PLANNER_AGENT_ICONS = 10;

  const constructToolsListForAgentTools = (
    primaryAgent,
    agentHosting,
    chatObject,
    canToggleTools = false,
    allAgents = []
  ) => {
    const tools = [];
    const toggleableTools = getToggleableToolsForMode(primaryAgent?.mode);
    const toggleablePropertySet = new Set(
      toggleableTools.map((t) => t.property)
    );

    // Helper to get agent object by ID
    const getAgentById = (agentId) => {
      return allAgents?.find((a) => a?.id === agentId);
    };

    // Helper to check if a tool should be added as toggleable
    const addToolWithToggleCheck = (property, icon, description) => {
      const isToggleable = toggleablePropertySet.has(property);
      const isActive = primaryAgent?.[property];

      if (isToggleable && canToggleTools) {
        // Always show toggleable tools for authorized users
        tools.push({
          icon,
          description,
          isToggleable: true,
          property,
          isActive: !!isActive,
        });
      } else if (isActive) {
        // Only show if active for non-toggleable or non-authorized
        tools.push({ icon, description });
      }
    };

    if (chatObject?.handedOffToHuman) {
      return [{ icon: "handOff", description: t("chats.humanHandoff") }];
    }

    if (agentHosting?.type == "ollama") {
      return [{ icon: "ollama", description: t("chats.ollamaHostingNoTools") }];
    }

    if (primaryAgent?.mode == "planner") {
      const agentsPool = primaryAgent?.agentsPool || [];
      const agentCount = agentsPool.length;
      const canAutoSpawn = primaryAgent?.plannerAutoAgentSpawn === true;

      // Add visibility icons first
      if (chatObject?.visibility === "incognito")
        tools.push({
          icon: "incognito",
          description: t("chats.tools.incognitoConversation"),
        });
      if (chatObject?.visibility === "private")
        tools.push({
          icon: "private",
          description: t("chats.tools.privateConversation"),
        });
      if (chatObject?.visibility === "public")
        tools.push({
          icon: "users",
          description: t("chats.tools.publicConversation"),
        });

      if (agentCount === 0) {
        if (canAutoSpawn) {
          tools.push({
            icon: "planner-auto",
            description: t("chats.tools.canDynamicallyGenerateAgents"),
          });
        } else {
          tools.push({
            icon: "planner-empty",
            description: t("chats.tools.noAgentsAvailable"),
          });
        }
      } else {
        // Show individual agent icons (max 10)
        const agentsToShow = agentsPool.slice(0, MAX_PLANNER_AGENT_ICONS);
        const remainingCount = agentCount - MAX_PLANNER_AGENT_ICONS;

        agentsToShow.forEach((agentId) => {
          const agent = getAgentById(agentId);
          if (agent) {
            tools.push({
              isPlannerAgent: true,
              agentObject: agent,
              description: `${agent?.label || "Agent"} - ${mapModeWithLabel(
                agent?.mode
              )}`,
            });
          }
        });

        // Add "+X" indicator if there are more agents
        if (remainingCount > 0) {
          const pluralSuffix = remainingCount > 1 ? "s" : "";
          tools.push({
            isPlannerAgentOverflow: true,
            overflowCount: remainingCount,
            description: t("chats.tools.moreAgents", {
              count: remainingCount,
              plural: pluralSuffix,
            }),
          });
        }

        // Add dynamic generation indicator if enabled
        if (canAutoSpawn) {
          tools.push({
            icon: "planner-auto",
            description: t("chats.tools.canDynamicallyGenerateAgents"),
          });
        }
      }
    } else {
      if (
        primaryAgent?.hasTopicsContext &&
        primaryAgent?.allowedTopics?.length > 0
      ) {
        tools.push({
          icon: "notes",
          description: `Knowledge hub (${primaryAgent?.allowedTopics?.length})`,
        });
      }

      if (primaryAgent?.agentFunctions?.length > 0) {
        tools.push({
          icon: "advanced",
          description: `Tools ${
            primaryAgent?.agentFunctions?.length > 0 ? "" : ""
          }`,
        });
      }

      addToolWithToggleCheck(
        "allowDeepInternetSearch",
        "deep_search",
        t("chats.tools.deepSearch")
      );

      // Agentic cycle toggle - rotates through 3 states
      const agenticState = getAgenticCycleState(primaryAgent);
      const agenticConfig = AGENTIC_CYCLE_CONFIG[agenticState];
      const isToggleable = toggleablePropertySet.has("agenticCycle");

      if (isToggleable && canToggleTools) {
        tools.push({
          icon: agenticConfig.icon,
          description: agenticConfig.description,
          isToggleable: true,
          property: "agenticCycle",
          isActive: agenticState !== 0, // Active if not in NONE state
          isCycleToggle: true,
          cycleState: agenticState,
          cycleColor: agenticConfig.color,
        });
      }

      if (
        primaryAgent?.mode == "computer" ||
        ((primaryAgent?.agenticRAG || primaryAgent?.useInterleavedReasoning) &&
          primaryAgent?.agentDesktopVM)
      ) {
        tools.push({
          icon: "desktop",
          description: t("chats.tools.virtualDesktop"),
        });
      }

      // Toggleable tool - code execution
      if (!primaryAgent?.useInterleavedReasoning) {
        addToolWithToggleCheck(
          "hasCode",
          "code",
          t("chats.tools.codeExecution")
        );
      }

      if (primaryAgent?.llmBaseModel == "dynamic") {
        tools.push({
          icon: "route",
          description: t("chats.tools.dynamicRouting"),
        });
      }

      if (primaryAgent?.isLongTermMemoryEnabled) {
        tools.push({
          icon: "memory",
          description: t("chats.tools.longTermMemory"),
        });
      }

      // Toggleable tool - agent introspection
      addToolWithToggleCheck(
        "allowIntrospection",
        "introspection",
        t("chats.tools.agentIntrospection")
      );

      if (primaryAgent?.phoneNumberAsSender) {
        tools.push({
          icon: "sms",
          description: t("chats.tools.smsEnabled"),
        });
      }
      if (primaryAgent?.whatsappNumberAsSender) {
        tools.push({
          icon: "messaging",
          description: t("chats.tools.whatsappEnabled"),
        });
      }
      if (primaryAgent?.emailAsSender || primaryAgent?.allowEmailToAgent) {
        tools.push({
          icon: "email",
          description: t("chats.tools.emailEnabled"),
        });
      }

      if (primaryAgent?.hasModeration) {
        tools.push({
          icon: "moderation",
          description: t("chats.tools.moderation"),
        });
      }
      if (primaryAgent?.promptBuffering) {
        tools.push({
          icon: "promptBuffering",
          description: t("chats.tools.promptBuffering"),
        });
      }

      // Toggleable tool - charting (not available for Agentic 2.0)
      if (!primaryAgent?.useInterleavedReasoning) {
        addToolWithToggleCheck(
          "charting",
          "charting",
          t("chats.tools.charting")
        );
      }

      // Toggleable tools - media generation
      addToolWithToggleCheck(
        "allowImagesGeneration",
        "image-gen",
        t("chats.tools.imageGeneration")
      );
      addToolWithToggleCheck(
        "allowVideosGeneration",
        "video-gen",
        t("chats.tools.videoGeneration")
      );
      addToolWithToggleCheck(
        "allow3dModelGeneration",
        "3d-gen",
        t("chats.tools.3dModelGeneration")
      );

      if (chatObject?.visibility === "incognito")
        tools.push({
          icon: "incognito",
          description: t("chats.tools.incognitoConversation"),
        });
      if (chatObject?.visibility === "private")
        tools.push({
          icon: "private",
          description: t("chats.tools.privateConversation"),
        });
      if (chatObject?.visibility === "public")
        tools.push({
          icon: "users",
          description: t("chats.tools.publicConversation"),
        });
    }

    return tools.length > 0
      ? tools
      : [{ icon: "no-tools", description: t("chats.tools.noToolsAvailable") }];
  };

  function getLeftIcon(type, AppAssets) {
    // Map file extensions to types
    const extensionToTypeMap = {
      // Audio files
      mp3: "audio",
      wav: "audio",
      ogg: "audio",
      m4a: "audio",

      // Video files
      mp4: "video",
      avi: "video",
      mov: "video",
      wmv: "video",

      // Programming files
      html: "programming",
      css: "programming",
      js: "programming",
      py: "programming",
      java: "programming",
      cpp: "programming",

      // Spreadsheet files
      xlsx: "spreadsheet",
      xls: "spreadsheet",
      csv: "spreadsheet",

      // Document files
      doc: "document",
      docx: "document",
      pdf: "document",
      txt: "document",

      // Presentation files
      ppt: "presentation",
      pptx: "presentation",

      // Image files (you might want to add an 'image' type to your iconMap)
      jpg: "image",
      jpeg: "image",
      png: "image",
      gif: "image",
      svg: "image",
    };

    // Get the file type based on extension
    const fileType = extensionToTypeMap[type] || "unknown";
    const iconMap = {
      audio: AppAssets?.icons?.audio,
      video: AppAssets?.icons?.video,
      programming: AppAssets?.icons?.html,
      spreadsheet: AppAssets?.icons?.spreadsheet,
      document: AppAssets?.icons?.document,
      presentation: AppAssets?.icons?.presentation,
      image: AppAssets?.icons?.image,
    };

    return iconMap[fileType] || AppAssets?.icons?.file;
  }

  function getFileNameAndExtension(path) {
    // Check if the input is a non-empty string
    if (typeof path !== "string" || path.trim() === "") {
      return { fileName: "", extension: "" };
    }

    // Split the path by '/' and get the last part
    const parts = path.split("/");
    const lastPart = parts[parts.length - 1];

    // Split the last part by '.' to separate filename and extension
    const fileParts = lastPart.split(".");

    // If there's no extension, return the whole filename
    if (fileParts.length === 1) {
      return { fileName: lastPart, extension: "" };
    }

    // Get the extension (last part after the dot)
    const extension = fileParts.pop();

    // Join the remaining parts as the filename
    const fileName = fileParts.join(".");

    return { fileName, extension, fullName: lastPart };
  }

  const _artifactsModalView = useMemo(() => {
    return (
      <ToothFairyModal
        transparent
        modalStyle={
          Platform.OS === "web"
            ? {
                width: "95%",
                maxWidth: 600,
                height: "auto",
                marginHorizontal: "auto",
                marginTop: "auto",
                marginBottom: "auto",
                borderRadius: 20,
                maxHeight: "90%",
              }
            : {}
        }
        isModalVisible={isArtifactsModalShown}
        animationType="none"
        modalBackdrop={currentTheme?.theme?.softBackgroundColor}
        isCloseButtonVisible
        modalTitle={t("chats.modals.artifactsAvailable")}
        modalInnerContentStyle={{
          paddingHorizontal: 20,
          paddingVertical: 20,
        }}
        closeModal={() => {
          setShowArtifactsModal(false);
        }}
      >
        <View
          style={{
            gap: 5,
          }}
        >
          {planArtifacts?.map((artifact) => {
            const { fileName, extension, fullName } = getFileNameAndExtension(
              artifact?.file_key
            );
            return (
              <AppText
                wrapperStyle={{
                  alignItems: "flex-start",
                  flexDirection: "row",
                  justifyContent: "flex-start",
                  gap: 5,
                  paddingVertical: 5,
                }}
                iconSize={16}
                leftIcon={getLeftIcon(extension, AppAssets)}
                key={artifact?.file_key}
                // rightIcon={AppAssets?.icons?.goTo}
                onPress={() => {
                  fetchStepArtifact(artifact?.file_key);
                }}
                isClickable
              >
                {`File name: ${fullName}\nStep definition: ${artifact?.description_from_step_definition}`}
              </AppText>
            );
          })}
        </View>
      </ToothFairyModal>
    );
  }, [
    t,
    setShowArtifactsModal,
    isArtifactsModalShown,
    JSON.stringify(planArtifacts),
  ]);

  const _citationModalView = useMemo(() => {
    if (!selectedCitation) return null;
    const { source, citationNumber } = selectedCitation;
    const isWebSource = source?.type === "web";

    return (
      <ToothFairyModal
        transparent
        modalStyle={
          Platform.OS === "web"
            ? {
                width: "95%",
                maxWidth: 500,
                height: "auto",
                marginHorizontal: "auto",
                marginTop: "auto",
                marginBottom: "auto",
                borderRadius: 16,
                maxHeight: "80%",
              }
            : {}
        }
        isModalVisible={isCitationModalShown}
        animationType="none"
        modalBackdrop={currentTheme?.theme?.softBackgroundColor}
        isCloseButtonVisible
        modalTitle={`Source [${citationNumber}]`}
        modalInnerContentStyle={{
          paddingHorizontal: 20,
          paddingVertical: 16,
        }}
        closeModal={() => {
          setShowCitationModal(false);
          setSelectedCitation(null);
        }}
      >
        <View style={{ gap: 16 }}>
          {/* Source Type Badge */}
          <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
            <View
              style={{
                paddingRight: 0,
                paddingVertical: 4,
                borderRadius: 6,
              }}
            >
              <AppText
                style={{
                  fontSize: 12,
                  fontWeight: "600",
                  color: isWebSource
                    ? currentTheme?.theme?.green || "#16a34a"
                    : currentTheme?.theme?.primary,
                }}
                preventTranslation
              >
                {isWebSource ? "🌐 Web Source" : "📄 Knowledge Hub Document"}
              </AppText>
            </View>
            {source?.scoring && (
              <AppText
                style={{
                  fontSize: 12,
                  color: currentTheme?.theme?.darkGrey,
                }}
                wrapperStyle={{
                  marginTop: 2,
                }}
                preventTranslation
              >
                - Score: {Math.round(source.scoring * 100)}%
              </AppText>
            )}
          </View>

          {/* Title */}
          <View>
            <AppText
              style={{
                fontSize: 11,
                color: currentTheme?.theme?.darkGrey,
                marginBottom: 4,
              }}
            >
              {t("chats.ui.title")}
            </AppText>
            <AppText
              style={{
                fontSize: 14,
                fontWeight: "600",
                color: currentTheme?.theme?.font_color,
              }}
              numberOfLines={2}
            >
              {source?.title || t("chats.ui.untitledSource")}
            </AppText>
          </View>

          {/* URL/Link */}
          {(source?.link || source?.url) && (
            <View>
              <AppText
                style={{
                  fontSize: 11,
                  color: currentTheme?.theme?.darkGrey,
                  marginBottom: 4,
                }}
              >
                {t("chats.ui.sourceURL")}
              </AppText>
              <AppText
                isClickable
                onPress={() => {
                  const url = source?.link || source?.url;
                  if (url) {
                    window.open(url, "_blank");
                  }
                }}
                style={{
                  fontSize: 13,
                  color: currentTheme?.theme?.primary,
                }}
                numberOfLines={2}
                preventTranslation
              >
                {source?.link || source?.url}
              </AppText>
            </View>
          )}

          {/* Passage/Snippet */}
          {(source?.passage || source?.snippet) && (
            <View>
              <AppText
                style={{
                  fontSize: 11,
                  color: currentTheme?.theme?.darkGrey,
                  marginBottom: 4,
                }}
              >
                {t("chats.ui.citedPassage")}
              </AppText>
              <View
                style={{
                  backgroundColor: currentTheme?.theme?.lightGrey || "#f9fafb",
                  borderLeftWidth: 3,
                  borderLeftColor: currentTheme?.theme?.primary,
                  paddingHorizontal: 12,
                  paddingVertical: 10,
                  borderRadius: 4,
                }}
              >
                <AppText
                  style={{
                    fontSize: 13,
                    lineHeight: 20,
                    color: currentTheme?.theme?.darkGrey,
                  }}
                  preventTranslation
                >
                  {source?.passage || source?.snippet}
                  {source?.truncated && (
                    <AppText
                      style={{
                        fontStyle: "italic",
                        color: currentTheme?.theme?.mediumGrey,
                      }}
                      preventTranslation
                    >
                      {" "}
                      {t("chats.ui.truncated")}
                    </AppText>
                  )}
                </AppText>
              </View>
            </View>
          )}
        </View>
      </ToothFairyModal>
    );
  }, [t, isCitationModalShown, selectedCitation, currentTheme]);

  // Provider options for model selection
  const providerOptions = useMemo(() => {
    return [
      { name: "ToothFairyAI", id: HostingType.TF },
      { name: "Together AI", id: HostingType.TOGETHER },
      { name: "OpenAI", id: HostingType.OPENAI },
      { name: "Anthropic", id: HostingType.ANTHROPIC },
      { name: "Google", id: HostingType.GOOGLE },
      { name: "Azure OpenAI", id: HostingType.AZURE_OPENAI },
      { name: "AWS Bedrock", id: HostingType.AWS },
      { name: "Groq", id: HostingType.GROQ },
      { name: "Replicate", id: HostingType.REPLICATE },
    ];
  }, []);

  // Get models filtered by provider
  const _enforceRegional =
    getActiveWorkspace()?.enforceRegionalModels === true ||
    getActiveWorkspace()?.enforceRegionalModels === "true";
  const _isRegionalActive =
    _enforceRegional ||
    selectedRegionalEndpoint === true ||
    selectedRegionalEndpoint === "true";
  const getModelsForProvider = useCallback(
    (provider) => {
      if (provider === HostingType.TF || provider === "tf") {
        // ToothFairyAI provider - show ALL base models (not just tfOnly), skip dynamic routing
        const _tfModels = _baseModels
          ?.filter(
            (model) =>
              !model.name?.includes("(Deprecated)") && model.id !== "dynamic"
          )
          ?.filter((model) => {
            if (_isRegionalActive && _regionalModelIds?.length > 0) {
              return _regionalModelIds.includes(model.id);
            }
            return true;
          })
          ?.map((model) => ({
            name: model.name,
            id: model.id,
            maxTokens: model.maxTokens,
            reasoner: model.reasoner,
            dynamicRouting: model.dynamicRouting,
            interleaved: model.interleaved,
          }));
        // Fine-tuned models ride on lanes in the workspace's own region, so
        // they are appended AFTER the regional filter instead of going through
        // it (their ids are never in the serverless regional list) — same
        // behaviour as the QA settings base-model dropdown.
        const _finetuned = (finetunedModels || [])?.map((f) => ({
          name: f?.name,
          id: f?.id,
          dynamicRouting: f?.dynamicRouting,
        }));
        return [...(_tfModels || []), ..._finetuned];
      } else if (provider === HostingType.TOGETHER || provider === "together") {
        // Together AI models - base models without tfOnly
        return _baseModels
          ?.filter(
            (model) => !model.tfOnly && !model.name?.includes("(Deprecated)")
          )
          ?.map((model) => ({
            name: model.name,
            id: model.id,
            reasoner: model.reasoner,
            interleaved: model.interleaved,
          }));
      } else {
        // External providers - get models from external models list
        const models =
          _externalModels?.filter((m) => m?.provider === provider) || [];
        const deprecatedModels =
          _externalDeprecatedModels?.filter((m) => m?.provider === provider) ||
          [];
        const allModels = [...models, ...deprecatedModels];
        if (allModels.length > 0) {
          return allModels.map((model) => ({
            name: model.name,
            id: model.id,
            reasoner: model.reasoner,
            interleaved: model.interleaved,
            toolCalling: model.toolCalling,
          }));
        }
        return [];
      }
    },
    [
      t,
      _baseModels,
      _externalModels,
      _externalDeprecatedModels,
      _isRegionalActive,
      _regionalModelIds,
      finetunedModels,
    ]
  );

  // Fine-tuned models come from the workspace's training logs, which are only
  // loaded by the Training screen — on a fresh session where Training was
  // never opened, state.trainingLogs is empty and the quick model selection
  // would miss every fine-tuned model. Fetch the logs here once when missing
  // (mirrors QaSettingsAIView), and recompute whenever the logs change.
  useEffect(() => {
    if (!state?.trainingLogs || state.trainingLogs.length === 0) {
      if (!workspaceid) return;
      let cancelled = false;
      trainingLogHelper
        .getTrainingLogsByWorkspaceID(workspaceid)
        .then((res) => {
          if (cancelled) return;
          const logs =
            res?.data?.trainingLogsByWorkspace?.items?.filter(
              (u) => u?._deleted != true
            ) || [];
          if (logs.length > 0) dispatch("SET_TRAINING_LOGS", logs);
        })
        .catch((e) => console.error("Failed to load training logs:", e));
      return () => {
        cancelled = true;
      };
    }
    setFinetunedModels(getFinetunedModels());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    getActiveWorkspace()?.subscriptionType,
    JSON.stringify(state?.trainingLogs),
    workspaceid,
  ]);

  // Initialize selected provider/model when modal opens
  useEffect(() => {
    if (isModelSelectionModalShown && primaryAgent) {
      setSelectedProvider(primaryAgent?.llmProvider || HostingType.TF);
      setSelectedRegionalEndpoint(
        _enforceRegional
          ? true
          : primaryAgent?.regionalEndpoint === true ||
              primaryAgent?.regionalEndpoint === "true"
      );
      // For planner mode, use functionCallingModel instead of llmBaseModel
      const modelToUse =
        primaryAgent?.mode === "planner"
          ? primaryAgent?.functionCallingModel
          : primaryAgent?.llmBaseModel;
      setSelectedModel(modelToUse || null);
    }
  }, [isModelSelectionModalShown, primaryAgent?.id]);

  const _topicsModalView = useMemo(() => {
    const allowedTopicsList = (() => {
      if (!primaryAgent?.allowedTopics) return [];
      if (typeof primaryAgent?.allowedTopics === "string") {
        try {
          return JSON.parse(primaryAgent?.allowedTopics);
        } catch {
          return [];
        }
      }
      return primaryAgent?.allowedTopics;
    })();

    return (
      <ToothFairyModal
        transparent
        modalStyle={{
          width: "95%",
          maxWidth: 600,
          maxHeight: "80%",
          height: "auto",
          marginHorizontal: "auto",
          marginTop: "auto",
          marginBottom: "auto",
          borderRadius: 16,
        }}
        isModalVisible={isTopicsModalOpen}
        animationType="none"
        modalBackdrop={currentTheme?.theme?.softBackgroundColor}
        isCloseButtonVisible
        modalTitle={t("chats.modals.knowledgeHubTopics")}
        modalInnerContentStyle={{
          paddingHorizontal: 20,
          paddingVertical: 16,
        }}
        closeModal={() => {
          setIsTopicsModalOpen(false);
        }}
      >
        <View style={{ gap: 12 }}>
          {!isLargeScreen() && (
            <AppText
              leftIcon={AppAssets?.icons?.arrow_left}
              iconSize={14}
              style={{
                fontSize: 14,
                color: currentTheme?.theme?.primary,
              }}
              wrapperStyle={{
                marginBottom: 8,
              }}
              isClickable
              onPress={() => {
                setIsTopicsModalOpen(false);
                setIsCollapsedToolsModalOpen(true);
              }}
            >
              {t("chats.ui.backToOverview")}
            </AppText>
          )}
          {allowedTopicsList.map((topic) => {
            return (
              <View
                key={topic?.id}
                style={{
                  backgroundColor: currentTheme?.theme?.softBackgroundColor,
                  borderRadius: 8,
                  padding: 12,
                }}
              >
                <AppText
                  style={{
                    fontSize: 14,
                    fontWeight: "600",
                    color: currentTheme?.theme?.font_color,
                    marginBottom: 4,
                  }}
                  preventTranslation
                >
                  {topic?.name || t("chats.ui.untitledTopic")}
                </AppText>
                <AppText
                  style={{
                    fontSize: 13,
                    color: currentTheme?.theme?.darkGrey,
                    marginBottom: 4,
                  }}
                  numberOfLines={2}
                  preventTranslation
                >
                  {topic?.description || t("chats.ui.noDescriptionProvided")}
                </AppText>
                <AppText
                  style={{
                    fontSize: 12,
                    color: currentTheme?.theme?.mediumGrey,
                    textTransform: "uppercase",
                  }}
                  preventTranslation
                >
                  {topic?.type || "Topic"}
                </AppText>
              </View>
            );
          })}
          {(!allowedTopicsList || allowedTopicsList?.length === 0) && (
            <AppText
              style={{
                fontSize: 14,
                color: currentTheme?.theme?.darkGrey,
                textAlign: "center",
                paddingVertical: 20,
              }}
              preventTranslation
            >
              {t("chats.ui.noTopicsConfigured")}
            </AppText>
          )}
        </View>
      </ToothFairyModal>
    );
  }, [
    isTopicsModalOpen,
    setIsTopicsModalOpen,
    currentTheme,
    JSON.stringify(primaryAgent?.allowedTopics),
    topics?.length,
    isLargeScreen(),
    t,
  ]);

  const _otherToolsModalView = useMemo(() => {
    const allTools = constructToolsListForAgentTools(
      primaryAgent,
      agentHosting,
      chatObject,
      canToggleTools,
      state?.agents
    );
    const otherStaticTools =
      allTools?.filter(
        (t) =>
          !t?.isToggleable &&
          t?.icon !== "notes" &&
          t?.icon !== "advanced" &&
          !t?.isPlannerAgent &&
          !t?.isPlannerAgentOverflow
      ) || [];

    return (
      <ToothFairyModal
        transparent
        modalStyle={{
          width: "95%",
          maxWidth: 600,
          maxHeight: "80%",
          height: "auto",
          marginHorizontal: "auto",
          marginTop: "auto",
          marginBottom: "auto",
          borderRadius: 16,
        }}
        isModalVisible={isOtherToolsModalOpen}
        animationType="none"
        modalBackdrop={currentTheme?.theme?.softBackgroundColor}
        isCloseButtonVisible
        modalTitle={t("chats.modals.otherTools")}
        modalInnerContentStyle={{
          paddingHorizontal: 20,
          paddingVertical: 16,
        }}
        closeModal={() => {
          setIsOtherToolsModalOpen(false);
        }}
      >
        <View style={{ gap: 12 }}>
          {!isLargeScreen() && (
            <AppText
              leftIcon={AppAssets?.icons?.arrow_left}
              iconSize={14}
              style={{
                fontSize: 14,
                color: currentTheme?.theme?.primary,
              }}
              wrapperStyle={{
                marginBottom: 8,
              }}
              isClickable
              onPress={() => {
                setIsOtherToolsModalOpen(false);
                setIsCollapsedToolsModalOpen(true);
              }}
            >
              {t("chats.ui.backToOverview")}
            </AppText>
          )}
          {otherStaticTools.map((tool, index) => (
            <View
              key={`other-tool-${index}-${tool?.icon}`}
              style={{
                flexDirection: "row",
                alignItems: "center",
                gap: 10,
                backgroundColor: currentTheme?.theme?.softBackgroundColor,
                borderRadius: 8,
                padding: 12,
              }}
            >
              <AppIcon
                icon={AppAssets?.icons[tool?.icon]}
                size={18}
                color={
                  tool?.icon === "handOff"
                    ? currentTheme?.theme?.orange
                    : currentTheme?.theme?.darkGrey
                }
              />
              <AppText
                style={{
                  fontSize: 14,
                  color: currentTheme?.theme?.font_color,
                  flex: 1,
                }}
                isClickable={tool?.icon === "handOff"}
                onPress={() => {
                  if (tool?.icon === "handOff") {
                    setIsOtherToolsModalOpen(false);
                    onHandOffToggle(!chatObject?.handedOffToHuman);
                  }
                }}
                preventTranslation
              >
                {tool?.description}
              </AppText>
            </View>
          ))}
          {otherStaticTools.length === 0 && (
            <AppText
              style={{
                fontSize: 14,
                color: currentTheme?.theme?.darkGrey,
                textAlign: "center",
                paddingVertical: 20,
              }}
              preventTranslation
            >
              {t("chats.ui.noOtherToolsAvailable")}
            </AppText>
          )}
        </View>
      </ToothFairyModal>
    );
  }, [
    isOtherToolsModalOpen,
    setIsOtherToolsModalOpen,
    currentTheme,
    JSON.stringify(primaryAgent),
    JSON.stringify(agentHosting),
    JSON.stringify(chatObject),
    canToggleTools,
    JSON.stringify(state?.agents),
    t,
  ]);

  const _toolsModalView = useMemo(() => {
    return (
      <ToothFairyModal
        transparent
        modalStyle={{
          width: "95%",
          maxWidth: 600,
          maxHeight: "80%",
          height: "auto",
          marginHorizontal: "auto",
          marginTop: "auto",
          marginBottom: "auto",
          borderRadius: 16,
        }}
        isModalVisible={isToolsModalOpen}
        animationType="none"
        modalBackdrop={currentTheme?.theme?.softBackgroundColor}
        isCloseButtonVisible
        modalTitle={t("chats.modals.agentTools")}
        modalInnerContentStyle={{
          paddingHorizontal: 20,
          paddingVertical: 16,
        }}
        closeModal={() => {
          setIsToolsModalOpen(false);
        }}
      >
        <View style={{ gap: 12 }}>
          {!isLargeScreen() && (
            <AppText
              leftIcon={AppAssets?.icons?.arrow_left}
              iconSize={14}
              style={{
                fontSize: 14,
                color: currentTheme?.theme?.primary,
              }}
              wrapperStyle={{
                marginBottom: 8,
              }}
              isClickable
              onPress={() => {
                setIsToolsModalOpen(false);
                setIsCollapsedToolsModalOpen(true);
              }}
            >
              {t("chats.ui.backToOverview")}
            </AppText>
          )}
          {resolvedAgentFunctions?.map((func) => (
            <View
              key={func.id}
              style={{
                backgroundColor: currentTheme?.theme?.softBackgroundColor,
                borderRadius: 8,
                padding: 12,
              }}
            >
              <AppText
                style={{
                  fontSize: 14,
                  fontWeight: "600",
                  color: currentTheme?.theme?.font_color,
                  marginBottom: 4,
                }}
                preventTranslation
              >
                {func.name}
              </AppText>
              <AppText
                style={{
                  fontSize: 13,
                  color: currentTheme?.theme?.darkGrey,
                  marginBottom: 4,
                }}
                numberOfLines={2}
                preventTranslation
              >
                {func.description || t("chats.ui.noDescriptionProvided")}
              </AppText>
              <AppText
                style={{
                  fontSize: 12,
                  color: currentTheme?.theme?.mediumGrey,
                  textTransform: "uppercase",
                }}
                preventTranslation
              >
                {func.isDatabaseScript
                  ? t("chats.ui.databaseScript")
                  : func.isMCPServer
                  ? t("chats.ui.mcpServer")
                  : func.isHtmlFn
                  ? t("chats.ui.html")
                  : func.isChatFn
                  ? t("chats.ui.chat")
                  : func.isCodeTemplate
                  ? t("chats.ui.codeTemplate")
                  : func.requestType?.toUpperCase() || t("chats.ui.api")}
              </AppText>
            </View>
          ))}
          {(!resolvedAgentFunctions ||
            resolvedAgentFunctions?.length === 0) && (
            <AppText
              style={{
                fontSize: 14,
                color: currentTheme?.theme?.darkGrey,
                textAlign: "center",
                paddingVertical: 20,
              }}
              preventTranslation
            >
              {t("chats.ui.noToolsConfigured")}
            </AppText>
          )}
        </View>
      </ToothFairyModal>
    );
  }, [
    isToolsModalOpen,
    setIsToolsModalOpen,
    currentTheme,
    JSON.stringify(resolvedAgentFunctions),
    isLargeScreen(),
    t,
  ]);

  const _queueModalView = useMemo(() => {
    const pendingMessages = pendingUserMessages;

    return (
      <ToothFairyModal
        transparent
        modalStyle={{
          width: "95%",
          maxWidth: 600,
          maxHeight: "80%",
          height: "auto",
          marginHorizontal: "auto",
          marginTop: "auto",
          marginBottom: "auto",
          borderRadius: 16,
        }}
        isModalVisible={isQueueModalOpen}
        animationType="none"
        modalBackdrop={currentTheme?.theme?.softBackgroundColor}
        isCloseButtonVisible
        modalTitle={t("chats.modals.messagesInQueue")}
        modalInnerContentStyle={{
          paddingHorizontal: 20,
          paddingVertical: 16,
        }}
        closeModal={() => {
          setIsQueueModalOpen(false);
        }}
      >
        <View style={{ gap: 12 }}>
          {pendingMessages?.map((msg, index) => (
            <View
              key={msg?._id || msg?.id || index}
              style={{
                backgroundColor: currentTheme?.theme?.softBackgroundColor,
                borderRadius: 8,
                padding: 12,
              }}
            >
              <AppText
                style={{
                  fontSize: 13,
                  color: currentTheme?.theme?.font_color,
                }}
                numberOfLines={3}
                preventTranslation
              >
                {msg?.text || t("chats.ui.noContent")}
              </AppText>
              <AppText
                style={{
                  fontSize: 11,
                  color: currentTheme?.theme?.mediumGrey,
                  marginTop: 4,
                }}
                preventTranslation
              >
                {t("chats.ui.statusPending")}
              </AppText>
            </View>
          ))}
          {(!pendingMessages || pendingMessages?.length === 0) && (
            <AppText
              style={{
                fontSize: 14,
                color: currentTheme?.theme?.darkGrey,
                textAlign: "center",
                paddingVertical: 20,
              }}
              preventTranslation
            >
              {t("chats.ui.noMessagesInQueue")}
            </AppText>
          )}
        </View>
      </ToothFairyModal>
    );
  }, [
    t,
    isQueueModalOpen,
    setIsQueueModalOpen,
    currentTheme,
    pendingUserMessages,
  ]);

  const _collapsedToolsModalView = useMemo(() => {
    const allTools = constructToolsListForAgentTools(
      primaryAgent,
      agentHosting,
      chatObject,
      canToggleTools,
      state?.agents
    );
    const staticTools = allTools?.filter((t) => !t?.isToggleable) || [];
    const toggleableTools = allTools?.filter((t) => t?.isToggleable) || [];

    const pendingMessages = pendingUserMessages;

    return (
      <ToothFairyModal
        transparent
        modalStyle={{
          width: "95%",
          maxWidth: 400,
          maxHeight: "80%",
          height: "auto",
          marginHorizontal: "auto",
          marginTop: "auto",
          marginBottom: "auto",
          borderRadius: 16,
        }}
        isModalVisible={isCollapsedToolsModalOpen}
        animationType="none"
        modalBackdrop={currentTheme?.theme?.softBackgroundColor}
        isCloseButtonVisible
        modalTitle={t("chats.modals.agentToolsAndInfo")}
        modalInnerContentStyle={{
          paddingHorizontal: 20,
          paddingVertical: 16,
        }}
        closeModal={() => {
          setIsCollapsedToolsModalOpen(false);
        }}
      >
        <View style={{ gap: 16 }}>
          {toggleableTools.length > 0 && (
            <View>
              <AppText
                style={{
                  fontSize: 12,
                  fontWeight: "bold",
                  color: currentTheme?.theme?.darkGrey,
                  marginBottom: 8,
                  textTransform: "uppercase",
                }}
              >
                {t("chats.ui.toggleableTools")}
              </AppText>
              <View style={{ gap: 8 }}>
                {toggleableTools.map((tool, index) => {
                  const isBeingToggled =
                    toolToggleInProgress === tool?.property;

                  // Handle cycle toggle differently
                  let iconColor;
                  let statusBadgeColor;
                  let statusBadgeBg;
                  let statusText;

                  if (tool?.isCycleToggle) {
                    // Cycle toggle uses state-specific colors
                    iconColor =
                      currentTheme?.theme?.[tool?.cycleColor] ||
                      currentTheme?.theme?.darkGrey;
                    statusBadgeColor =
                      currentTheme?.theme?.[tool?.cycleColor] ||
                      currentTheme?.theme?.mediumGrey;
                    statusBadgeBg =
                      (currentTheme?.theme?.[tool?.cycleColor] ||
                        currentTheme?.theme?.mediumGrey) + "20";
                    statusText = tool?.description; // Shows state name like "No agentic", "Agentic", "Agentic 2.0"
                  } else {
                    // Regular toggle uses orange when active, grey when inactive
                    iconColor = tool?.isActive
                      ? currentTheme?.theme?.orange
                      : currentTheme?.theme?.darkGrey;
                    statusBadgeColor = tool?.isActive
                      ? currentTheme?.theme?.orange
                      : currentTheme?.theme?.mediumGrey;
                    statusBadgeBg = tool?.isActive
                      ? currentTheme?.theme?.orange + "20"
                      : currentTheme?.theme?.lightGrey + "20";
                    statusText = tool?.isActive ? "ON" : "OFF";
                  }

                  return (
                    <View
                      key={`collapsed-toggle-${index}-${tool?.icon}`}
                      style={{
                        flexDirection: "row",
                        alignItems: "center",
                        justifyContent: "space-between",
                        backgroundColor:
                          currentTheme?.theme?.softBackgroundColor,
                        borderRadius: 8,
                        padding: 12,
                        opacity: isBeingToggled ? 0.5 : 1,
                        cursor: !toolToggleInProgress ? "pointer" : "default",
                      }}
                      onClick={
                        !toolToggleInProgress
                          ? () => {
                              onToolToggle(tool?.property, tool?.isActive);
                            }
                          : undefined
                      }
                    >
                      <View
                        style={{
                          flexDirection: "row",
                          alignItems: "center",
                          gap: 10,
                          flex: 1,
                        }}
                      >
                        <AppIcon
                          icon={AppAssets?.icons[tool?.icon]}
                          size={18}
                          color={iconColor}
                        />
                        <AppText
                          style={{
                            fontSize: 14,
                            color: currentTheme?.theme?.font_color,
                          }}
                          preventTranslation
                        >
                          {tool?.description}
                        </AppText>
                      </View>
                      <AppText
                        style={{
                          fontSize: 12,
                          fontWeight: "bold",
                          paddingHorizontal: 8,
                          paddingVertical: 4,
                          borderRadius: 4,
                          backgroundColor: statusBadgeBg,
                          color: statusBadgeColor,
                        }}
                        preventTranslation
                      >
                        {statusText}
                      </AppText>
                    </View>
                  );
                })}
              </View>
            </View>
          )}

          {staticTools.length > 0 && (
            <View>
              <AppText
                style={{
                  fontSize: 12,
                  fontWeight: "bold",
                  color: currentTheme?.theme?.darkGrey,
                  marginBottom: 8,
                  textTransform: "uppercase",
                }}
              >
                {t("chats.ui.activeFeatures")}
              </AppText>
              <View style={{ gap: 8 }}>
                {staticTools
                  .filter(
                    (tool) =>
                      tool?.icon === "notes" ||
                      tool?.icon === "advanced" ||
                      tool?.isPlannerAgent ||
                      tool?.isPlannerAgentOverflow
                  )
                  .map((tool, index) => {
                    if (tool?.isPlannerAgent) {
                      return (
                        <View
                          key={`collapsed-planner-${index}-${tool?.agentObject?.id}`}
                          style={{
                            flexDirection: "row",
                            alignItems: "center",
                            gap: 10,
                            backgroundColor:
                              currentTheme?.theme?.softBackgroundColor,
                            borderRadius: 8,
                            padding: 12,
                          }}
                        >
                          <AppIcon
                            icon={Utility.mapAgentTypeWithIcon(
                              tool?.agentObject,
                              AppAssets
                            )}
                            size={18}
                            color={currentTheme?.theme?.darkGrey}
                          />
                          <AppText
                            style={{
                              fontSize: 14,
                              color: currentTheme?.theme?.font_color,
                            }}
                            preventTranslation
                          >
                            {tool?.description}
                          </AppText>
                        </View>
                      );
                    }

                    if (tool?.isPlannerAgentOverflow) {
                      return (
                        <View
                          key={`collapsed-overflow-${index}`}
                          style={{
                            flexDirection: "row",
                            alignItems: "center",
                            gap: 10,
                            backgroundColor:
                              currentTheme?.theme?.softBackgroundColor,
                            borderRadius: 8,
                            padding: 12,
                          }}
                        >
                          <AppText
                            style={{
                              fontSize: 14,
                              fontWeight: "bold",
                              color: currentTheme?.theme?.darkGrey,
                            }}
                            preventTranslation
                          >
                            +{tool?.overflowCount}
                          </AppText>
                          <AppText
                            style={{
                              fontSize: 14,
                              color: currentTheme?.theme?.font_color,
                            }}
                            preventTranslation
                          >
                            {tool?.description}
                          </AppText>
                        </View>
                      );
                    }

                    const isClickable =
                      (tool?.icon === "notes" &&
                        primaryAgent?.allowedTopics?.length > 0) ||
                      (tool?.icon === "advanced" &&
                        primaryAgent?.agentFunctions?.length > 0);

                    return (
                      <View
                        key={`collapsed-static-${index}-${tool?.icon}`}
                        style={{
                          flexDirection: "row",
                          alignItems: "center",
                          gap: 10,
                          backgroundColor:
                            currentTheme?.theme?.softBackgroundColor,
                          borderRadius: 8,
                          padding: 12,
                        }}
                      >
                        <AppIcon
                          icon={AppAssets?.icons[tool?.icon]}
                          size={18}
                          color={currentTheme?.theme?.darkGrey}
                        />
                        <AppText
                          style={{
                            fontSize: 14,
                            color: currentTheme?.theme?.font_color,
                            flex: 1,
                          }}
                          isClickable={isClickable}
                          onPress={() => {
                            if (
                              tool?.icon === "notes" &&
                              primaryAgent?.allowedTopics?.length > 0
                            ) {
                              setIsCollapsedToolsModalOpen(false);
                              setIsTopicsModalOpen(true);
                            } else if (
                              tool?.icon === "advanced" &&
                              primaryAgent?.agentFunctions?.length > 0
                            ) {
                              setIsCollapsedToolsModalOpen(false);
                              setIsToolsModalOpen(true);
                            }
                          }}
                          preventTranslation
                        >
                          {tool?.description}
                          {isClickable && ` ${t("chats.ui.tapToView")}`}
                        </AppText>
                      </View>
                    );
                  })}
                {staticTools.filter(
                  (tool) =>
                    tool?.icon !== "notes" &&
                    tool?.icon !== "advanced" &&
                    !tool?.isPlannerAgent &&
                    !tool?.isPlannerAgentOverflow
                ).length > 0 && (
                  <View
                    key="collapsed-other-tools"
                    style={{
                      flexDirection: "row",
                      alignItems: "center",
                      gap: 10,
                      backgroundColor: currentTheme?.theme?.softBackgroundColor,
                      borderRadius: 8,
                      padding: 12,
                    }}
                  >
                    <AppIcon
                      icon={AppAssets?.icons?.settings}
                      size={18}
                      color={currentTheme?.theme?.darkGrey}
                    />
                    <AppText
                      style={{
                        fontSize: 14,
                        color: currentTheme?.theme?.font_color,
                        flex: 1,
                      }}
                      isClickable
                      onPress={() => {
                        setIsCollapsedToolsModalOpen(false);
                        setIsOtherToolsModalOpen(true);
                      }}
                      preventTranslation
                    >
                      {t("chats.modals.otherTools")}
                      {` ${t("chats.ui.tapToView")}`}
                    </AppText>
                  </View>
                )}
              </View>
            </View>
          )}

          <View>
            <AppText
              style={{
                fontSize: 12,
                fontWeight: "bold",
                color: currentTheme?.theme?.darkGrey,
                marginBottom: 8,
                textTransform: "uppercase",
              }}
            >
              {t("chats.ui.actions")}
            </AppText>
            <View style={{ gap: 8 }}>
              {planArtifacts?.length > 0 && (
                <View
                  style={{
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 10,
                    backgroundColor: currentTheme?.theme?.softBackgroundColor,
                    borderRadius: 8,
                    padding: 12,
                  }}
                >
                  <AppIcon
                    icon={AppAssets?.icons?.artifacts}
                    size={18}
                    color={currentTheme?.theme?.orange}
                  />
                  <AppText
                    style={{
                      fontSize: 14,
                      color: currentTheme?.theme?.font_color,
                      flex: 1,
                    }}
                    isClickable
                    onPress={() => {
                      setIsCollapsedToolsModalOpen(false);
                      setShowArtifactsModal(true);
                    }}
                    preventTranslation
                  >
                    {t("chats.ui.showArtifacts", {
                      count: planArtifacts?.length,
                    })}
                  </AppText>
                </View>
              )}
              {canViewUoIUsage && (
                <View
                  style={{
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 10,
                    backgroundColor: currentTheme?.theme?.softBackgroundColor,
                    borderRadius: 8,
                    padding: 12,
                  }}
                >
                  <AppIcon
                    icon={AppAssets?.icons?.consumedUoI}
                    size={18}
                    color={currentTheme?.theme?.primary}
                  />
                  <AppText
                    style={{
                      fontSize: 14,
                      color: currentTheme?.theme?.font_color,
                      flex: 1,
                    }}
                    isClickable
                    onPress={() => {
                      setIsCollapsedToolsModalOpen(false);
                      setShowUoIUsageModal(true);
                    }}
                    preventTranslation
                  >
                    {t("chats.ui.uoiUsage")}
                  </AppText>
                </View>
              )}

              <View
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 10,
                  backgroundColor: currentTheme?.theme?.softBackgroundColor,
                  borderRadius: 8,
                  padding: 12,
                  opacity: messages?.length > 0 ? 1 : 0.5,
                }}
              >
                <AppIcon
                  icon={AppAssets?.icons?.export}
                  size={18}
                  color={
                    messages?.length > 0
                      ? currentTheme?.theme?.primary
                      : currentTheme?.theme?.mediumGrey
                  }
                />
                <AppText
                  style={{
                    fontSize: 14,
                    color: currentTheme?.theme?.font_color,
                    flex: 1,
                  }}
                  isClickable={messages?.length > 0}
                  onPress={() => {
                    if (messages?.length > 0) {
                      setIsCollapsedToolsModalOpen(false);
                      createMessagesExportFile();
                    }
                  }}
                  preventTranslation
                >
                  {messages?.length > 0
                    ? t("chats.ui.exportChat")
                    : t("chats.ui.emptyChat")}
                </AppText>
              </View>

              {pendingMessages?.length > 0 && (
                <View
                  style={{
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 10,
                    backgroundColor: currentTheme?.theme?.softBackgroundColor,
                    borderRadius: 8,
                    padding: 12,
                  }}
                >
                  <AppIcon
                    icon={AppAssets?.icons?.queue}
                    size={18}
                    color={currentTheme?.theme?.orange}
                  />
                  <AppText
                    style={{
                      fontSize: 14,
                      color: currentTheme?.theme?.font_color,
                      flex: 1,
                    }}
                    isClickable
                    onPress={() => {
                      setIsCollapsedToolsModalOpen(false);
                      setIsQueueModalOpen(true);
                    }}
                    preventTranslation
                  >
                    {t("chats.ui.messagesInQueue", {
                      count: pendingMessages?.length,
                      plural: pendingMessages?.length > 1 ? "s" : "",
                    })}
                  </AppText>
                </View>
              )}
            </View>
          </View>
        </View>
      </ToothFairyModal>
    );
  }, [
    t,
    isCollapsedToolsModalOpen,
    setIsCollapsedToolsModalOpen,
    currentTheme,
    JSON.stringify(primaryAgent),
    JSON.stringify(agentHosting),
    JSON.stringify(chatObject),
    canToggleTools,
    toolToggleInProgress,
    JSON.stringify(messages),
    JSON.stringify(planArtifacts),
    JSON.stringify(state?.agents),
  ]);

  const _modelSelectionModalView = useMemo(() => {
    const isVoiceAgent = primaryAgent?.mode === "voice";
    const _allModelsForProvider = isVoiceAgent
      ? LLM_VOICE_MODELS
      : getModelsForProvider(selectedProvider);
    const modelsForProvider = primaryAgent?.useInterleavedReasoning
      ? _allModelsForProvider?.filter((m) => m?.interleaved === true) || []
      : _allModelsForProvider;
    const isTFProvider =
      selectedProvider === HostingType.TF || selectedProvider === "tf";
    const selectedModelDetails = modelsForProvider?.find(
      (m) => m.id === selectedModel
    );

    if (isVoiceAgent) {
      return (
        <ToothFairyModal
          transparent
          modalStyle={
            Platform.OS === "web"
              ? {
                  width: "95%",
                  maxWidth: 500,
                  height: "auto",
                  marginHorizontal: "auto",
                  marginTop: "auto",
                  marginBottom: "auto",
                  borderRadius: 16,
                  maxHeight: "80%",
                }
              : {}
          }
          isModalVisible={isModelSelectionModalShown}
          animationType="none"
          modalBackdrop={currentTheme?.theme?.softBackgroundColor}
          isCloseButtonVisible
          modalTitle={t("chats.modals.changeLLMModelForVoice")}
          modalInnerContentStyle={{
            paddingHorizontal: 20,
            paddingVertical: 16,
          }}
          closeModal={() => {
            setShowModelSelectionModal(false);
            setSelectedModel(null);
          }}
        >
          <View style={{ gap: 16 }}>
            <View style={{ zIndex: 1000 }}>
              <AppDropDown
                showTextInputLabel
                label={t("chats.ui.llmModelForVoice")}
                inputLabelStyle={{
                  marginLeft: 0,
                  marginBottom: 8,
                }}
                dropDownStyle={{
                  width: "100%",
                  borderColor: currentTheme?.theme?.border_color,
                }}
                isElevated={false}
                items={LLM_VOICE_MODELS}
                propertyLabel="name"
                propertyValue="id"
                selectedValue={selectedModel}
                onValueChange={(itemValue) => {
                  setSelectedModel(itemValue);
                }}
              />
            </View>

            {selectedModel && (
              <ModelPricingInfo
                modelId={selectedModel}
                type="llm"
                showDynamicRouting={false}
                style={{ marginTop: 8, marginBottom: 12 }}
              />
            )}

            <View
              style={{
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
                marginTop: 16,
              }}
            >
              <ToothFairyButton
                title={t("chats.ui.cancel")}
                wrapperStyle={{}}
                isDeletion
                fontColor={currentTheme?.theme?.mediumGrey}
                onPress={() => {
                  setShowModelSelectionModal(false);
                  setSelectedModel(null);
                }}
              />
              <ToothFairyButton
                title={
                  isModelChangeInProgress
                    ? t("chats.ui.saving")
                    : t("chats.ui.saveChanges")
                }
                disabled={!selectedModel || isModelChangeInProgress}
                onPress={() => {
                  onBaseModelChange(null, selectedModel, null);
                  setShowModelSelectionModal(false);
                }}
              />
            </View>
          </View>
        </ToothFairyModal>
      );
    }

    return (
      <ToothFairyModal
        transparent
        modalStyle={
          Platform.OS === "web"
            ? {
                width: "95%",
                maxWidth: 500,
                height: "auto",
                marginHorizontal: "auto",
                marginTop: "auto",
                marginBottom: "auto",
                borderRadius: 16,
                maxHeight: "80%",
              }
            : {}
        }
        isModalVisible={isModelSelectionModalShown}
        animationType="none"
        modalBackdrop={currentTheme?.theme?.softBackgroundColor}
        isCloseButtonVisible
        modalTitle={
          primaryAgent?.mode === "planner"
            ? t("chats.modals.changePlanModel")
            : t("chats.modals.changeBaseModel")
        }
        modalInnerContentStyle={{
          paddingHorizontal: 20,
          paddingVertical: 16,
        }}
        closeModal={() => {
          setShowModelSelectionModal(false);
          setSelectedProvider(null);
          setSelectedModel(null);
        }}
      >
        <View style={{ gap: 16 }}>
          <View style={{ zIndex: 1000 }}>
            <AppDropDown
              showTextInputLabel
              label={t("chats.ui.provider")}
              inputLabelStyle={{
                marginLeft: 0,
                marginBottom: 8,
              }}
              dropDownStyle={{
                width: "100%",
                borderColor: currentTheme?.theme?.border_color,
              }}
              isElevated={false}
              items={
                _enforceRegional
                  ? [{ name: "ToothFairyAI", id: HostingType.TF }]
                  : providerOptions
              }
              propertyLabel="name"
              propertyValue="id"
              selectedValue={selectedProvider}
              onValueChange={(itemValue) => {
                setSelectedProvider(itemValue);
                setSelectedModel(null);
                setSelectedReasoningEffort(null);
              }}
            />
          </View>

          {(selectedProvider === HostingType.TF ||
            selectedProvider === "tf") && (
            <View style={{ zIndex: 999, marginTop: 10 }}>
              <AppDropDown
                showTextInputLabel
                label={t("settings.qa.endpointType")}
                inputLabelStyle={{
                  marginLeft: 0,
                  marginBottom: 8,
                }}
                dropDownStyle={{
                  width: "100%",
                  borderColor: currentTheme?.theme?.border_color,
                }}
                isElevated={false}
                items={
                  _enforceRegional
                    ? [{ name: t("settings.qa.regional"), id: true }]
                    : [
                        { name: t("settings.qa.global"), id: false },
                        { name: t("settings.qa.regional"), id: true },
                      ]
                }
                propertyLabel="name"
                propertyValue="id"
                selectedValue={
                  _enforceRegional ? true : selectedRegionalEndpoint
                }
                disabled={_enforceRegional}
                onValueChange={(itemValue) => {
                  setSelectedRegionalEndpoint(itemValue);
                  setSelectedModel(null);
                }}
              />
            </View>
          )}

          {selectedProvider && modelsForProvider?.length > 0 && (
            <View style={{ zIndex: 998, marginTop: 10 }}>
              <AppDropDown
                showTextInputLabel
                label={t("chats.ui.model")}
                inputLabelStyle={{
                  marginLeft: 0,
                  marginBottom: 8,
                }}
                dropDownStyle={{
                  width: "100%",
                  borderColor: currentTheme?.theme?.border_color,
                }}
                isElevated={false}
                items={modelsForProvider}
                propertyLabel="name"
                propertyValue="id"
                selectedValue={selectedModel}
                onValueChange={(itemValue) => {
                  setSelectedModel(itemValue);
                  setSelectedReasoningEffort(null);
                }}
              />
            </View>
          )}

          {selectedModelDetails?.reasoner && (
            <View style={{ zIndex: 997, marginTop: 10 }}>
              <AppDropDown
                showTextInputLabel
                label={t("chats.ui.reasoningEffort")}
                inputLabelStyle={{
                  marginLeft: 0,
                  marginBottom: 8,
                }}
                dropDownStyle={{
                  width: "100%",
                  borderColor: currentTheme?.theme?.border_color,
                }}
                isElevated={false}
                items={[
                  { name: t("chats.ui.none"), id: "none" },
                  { name: t("chats.ui.low"), id: "low" },
                  { name: t("chats.ui.medium"), id: "medium" },
                  { name: t("chats.ui.high"), id: "high" },
                ]}
                propertyLabel="name"
                propertyValue="id"
                selectedValue={selectedReasoningEffort ?? "medium"}
                onValueChange={(itemValue) => {
                  setSelectedReasoningEffort(itemValue);
                }}
              />
            </View>
          )}

          {selectedModel && (
            <>
              <FineTunedModelStatusChip
                modelId={selectedModel}
                token={getActiveWorkspace()?.workspaceToken}
              />
              <FineTunedModelPricingInfo
                modelId={selectedModel}
                token={getActiveWorkspace()?.workspaceToken}
                style={{ marginTop: 8, marginBottom: 12 }}
                showReasonerBadge={true}
              />
            </>
          )}

          <View
            style={{
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              marginTop: 16,
            }}
          >
            <ToothFairyButton
              title={t("chats.ui.cancel")}
              wrapperStyle={{}}
              isDeletion
              fontColor={currentTheme?.theme?.mediumGrey}
              onPress={() => {
                setShowModelSelectionModal(false);
                setSelectedProvider(null);
                setSelectedModel(null);
                setSelectedReasoningEffort(null);
              }}
            />
            <ToothFairyButton
              title={
                isModelChangeInProgress
                  ? t("chats.ui.saving")
                  : t("chats.ui.saveChanges")
              }
              disabled={
                !selectedProvider || !selectedModel || isModelChangeInProgress
              }
              onPress={() => {
                const reasoningEffort = selectedModelDetails?.reasoner
                  ? selectedReasoningEffort ?? "medium"
                  : null;
                const regionalValue =
                  selectedProvider === HostingType.TF ||
                  selectedProvider === "tf"
                    ? _enforceRegional
                      ? true
                      : selectedRegionalEndpoint
                    : undefined;
                onBaseModelChange(
                  selectedProvider,
                  selectedModel,
                  reasoningEffort,
                  regionalValue
                );
                setShowModelSelectionModal(false);
              }}
            />
          </View>
        </View>
      </ToothFairyModal>
    );
  }, [
    t,
    isModelSelectionModalShown,
    selectedProvider,
    selectedModel,
    selectedReasoningEffort,
    currentTheme,
    providerOptions,
    getModelsForProvider,
    isModelChangeInProgress,
    onBaseModelChange,
    primaryAgent?.mode,
    _enforceRegional,
    _isRegionalActive,
    selectedRegionalEndpoint,
  ]);

  const _plannerModalView = useMemo(() => {
    return (
      <ToothFairyModal
        transparent
        modalStyle={
          Platform.OS === "web"
            ? {
                width: "95%",
                maxWidth: 600,
                height: "auto",
                marginHorizontal: "auto",
                marginTop: "auto",
                marginBottom: "auto",
                borderRadius: 20,
              }
            : {}
        }
        isModalVisible={isPlannerModalShown}
        animationType="none"
        modalBackdrop={currentTheme?.theme?.softBackgroundColor}
        isCloseButtonVisible
        modalTitle={t("chats.modals.agentsAvailable")}
        modalInnerContentStyle={{
          paddingHorizontal: 20,
          paddingVertical: 20,
        }}
        closeModal={() => {
          setShowPlannerModal(false);
        }}
      >
        <View
          style={{
            gap: 5,
          }}
        >
          {primaryAgent?.agentsPool?.map((agent) => {
            const _agent = getAgentObject(agent);
            const _group = _agent?.agentCategoryType
              ? agentGroupTopicById[_agent.agentCategoryType]
              : null;
            return (
              <View
                key={agent}
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <AppText
                  wrapperStyle={{
                    alignItems: "center",
                    flexDirection: "row",
                    justifyContent: "flex-start",
                    gap: 5,
                    paddingVertical: 5,
                    flex: 1,
                  }}
                  iconSize={16}
                  leftIcon={Utility.mapAgentTypeWithIcon(_agent, AppAssets)}
                  rightIcon={AppAssets?.icons?.goTo}
                  onRightIconPress={() => {
                    history.push(
                      `/workspaces/${getActiveWorkspace()?.id}/agents/${agent}`
                    );
                  }}
                >
                  {getAgentDescription(_agent)}
                </AppText>
                {!!_group?.label && (
                  <View
                    style={{
                      backgroundColor: `${
                        _group?.backgroundColor || currentTheme?.theme?.primary
                      }26`,
                      borderRadius: 12,
                      paddingHorizontal: 10,
                      paddingVertical: 4,
                      flexShrink: 0,
                    }}
                  >
                    <AppText
                      style={{
                        fontSize: 11,
                        color:
                          _group?.backgroundColor ||
                          currentTheme?.theme?.primary,
                        fontWeight: "500",
                      }}
                      numberOfLines={1}
                      preventTranslation
                    >
                      {_group?.emoji ? `${_group.emoji} ` : ""}
                      {_group.label}
                    </AppText>
                  </View>
                )}
              </View>
            );
          })}
        </View>
      </ToothFairyModal>
    );
  }, [t, setShowPlannerModal, isPlannerModalShown, agentGroupTopicById]);

  const _coreModalView = useMemo(() => {
    const handleFormSubmit = (values) => {
      if (treeViewActionContext === "NEW_CHAT") SaveChat({}, values, false);
      else if (treeViewActionContext === "EDIT_CHAT")
        SaveChat(selectedNode, values, true);
    };
    const generateModalTitle = () => {
      if (treeViewActionContext === "NEW_CHAT")
        return t("chats.modals.newChat");
      else if (treeViewActionContext === "EDIT_CHAT")
        return t("chats.modals.editChat");
    };
    return (
      <ToothFairyModal
        transparent
        modalStyle={
          Platform.OS === "web"
            ? {
                width: "95%",
                maxWidth: 600,
                height: "auto",
                maxHeight: "90%",
                marginHorizontal: "auto",
                marginTop: "auto",
                marginBottom: "auto",
                borderRadius: 20,
              }
            : {}
        }
        isModalVisible={showModal}
        animationType="none"
        modalBackdrop={currentTheme?.theme?.softBackgroundColor}
        isCloseButtonVisible
        modalTitle={generateModalTitle()}
        modalInnerContentStyle={{
          paddingHorizontal: 20,
          paddingVertical: 20,
        }}
        closeModal={() => {
          formRef?.current?.resetForm();
          setShowModal(false);
          setTreeViewActionContext("");
        }}
      >
        {treeViewActionContext === "DELETE_NODE" ? (
          <AppChoice
            wrapperStyle={{
              alignSelf: "center",
              maxWidth: 600,
              marginTop: 50,
            }}
            cancelCaption={t(`chats.${_selectedNodeType}DeleteCancelCaption`)}
            confirmCaption={t(`chats.${_selectedNodeType}DeleteConfirmCaption`)}
            onConfirmButtonPress={() => {
              deleteNode(selectedNodeToDelete);
            }}
            onCancelButtonPress={() => {
              setShowModal(false);
            }}
            AppChoiceButton={ToothFairyButton}
            caption={t(`chats.${_selectedNodeType}DeleteCaption`)}
            subCaption={t(`chats.${_selectedNodeType}DeleteSubCaption`)}
          />
        ) : (
          <ChatCreationForm
            ref={formRef}
            creationofNewDocumentInProgress={creationofNewDocumentInProgress}
            treeViewActionContext={treeViewActionContext}
            selectedNode={selectedNode}
            handleFormSubmit={handleFormSubmit}
            documents={documents}
          />
        )}
      </ToothFairyModal>
    );
  }, [
    t,
    setShowModal,
    SaveChat,
    // createDocument,
    currentTheme,
    showModal,
    isLargeScreen(),
    selectedNode?.id,
    treeViewActionContext,
    creationofNewDocumentInProgress,
    documents?.length,
  ]);

  const _coreSourcesModelView = useMemo(() => {
    return (
      <ToothFairyModal
        transparent
        modalStyle={
          Platform.OS === "web"
            ? {
                width: "95%",
                maxWidth: 600,
                maxHeight: "90%",
                minHeight: 300,
                height: "auto",
                marginHorizontal: "auto",
                marginTop: "auto",
                marginBottom: "auto",
                borderRadius: 20,
              }
            : {}
        }
        isModalVisible={showSourcesModal}
        animationType="none"
        modalBackdrop={currentTheme?.theme?.softBackgroundColor}
        isCloseButtonVisible
        modalTitle={t("chats.modals.toolsDetails")}
        modalInnerContentStyle={{
          paddingHorizontal: 20,
          paddingVertical: 20,
        }}
        closeModal={() => {
          setShowSourcesModal(false);
        }}
      >
        <ReferencesView
          selectedSources={selectedSources}
          S3downloadUrlRequest={S3downloadUrlRequest}
        />
      </ToothFairyModal>
    );
  }, [t, JSON.stringify(selectedSources), showSourcesModal]);

  const topicSelectionComponent = useMemo(() => {
    return (
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          paddingVertical: !isMobileScreen() && 10,
          zIndex: 1,
          marginLeft: 10,
          minWidth: "50%",
          flex: 1,
          // overflowX: "hidden",
        }}
      >
        <View
          style={{
            flexDirection: "row",
            justifyContent: "space-between",
            gap: 10,
          }}
        >
          {isAwaitingUserInput && (
            <Popable
              numberOfLines={12}
              backgroundColor={currentTheme?.theme?.tooltipBackgroundColor}
              style={[
                {
                  top: 30,
                  zIndex: 20,
                  maxWidth: 800,
                },
              ]}
              content={t("chats.ui.agentWaitingForFeedback")}
              action="hover"
              position="left"
            >
              <AppIcon
                icon={AppAssets?.icons?.preference}
                size={18}
                color={currentTheme?.theme?.primary}
                wrapperStyle={{
                  marginRight: 0,
                }}
              />
            </Popable>
          )}

          {isTfMainAgent && chatId && (
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                gap: 4,
                marginRight: 8,
              }}
            >
              <AppText
                style={{
                  fontWeight: "bold",
                  marginRight: 15,
                }}
                color={currentTheme?.theme?.primary}
                isClickable
                onPress={() => {
                  handleParentActionClick(homeAgentId || "tf_main");
                }}
              >
                {t("chats.ui.newChat")}
              </AppText>
              <AppText
                style={{ fontWeight: "bold" }}
                color={currentTheme?.theme?.darkGrey}
              >
                |
              </AppText>
            </View>
          )}
          <Popable
            numberOfLines={12}
            backgroundColor={currentTheme?.theme?.tooltipBackgroundColor}
            style={[
              {
                top: 30,
                zIndex: 20,
                width: 400,
              },
            ]}
            content={(() => {
              if (isTfMainAgent) {
                return t("planner.steps.baseModel", {
                  model: "mystica_15",
                });
              }
              const modelName =
                primaryAgent?.mode == "planner"
                  ? primaryAgent?.functionCallingModel
                  : primaryAgent?.llmBaseModel;
              const baseModelText = t("planner.steps.baseModel", {
                model: modelName,
              });
              const changeModelText = canChangeModel
                ? t("planner.steps.changeModel")
                : "";
              const content = baseModelText + changeModelText;
              return content.length > 80
                ? content.substring(0, 80) + "..."
                : content;
            })()}
            action="hover"
            position="left"
          >
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                gap: 4,
                cursor:
                  canChangeModel && !isAIStreaming && !isTfMainAgent
                    ? "pointer"
                    : "default",
              }}
            >
              <AppText
                style={{
                  fontWeight: "bold",
                }}
                color={currentTheme?.theme?.darkGrey}
                isClickable={canChangeModel && !isAIStreaming && !isTfMainAgent}
                onPress={() => {
                  if (canChangeModel && !isAIStreaming && !isTfMainAgent) {
                    setShowModelSelectionModal(true);
                  }
                }}
              >
                {isTfMainAgent ? "Fairy" : primaryAgent?.label}
              </AppText>
              {canChangeModel && !isTfMainAgent && (
                <AppIcon
                  icon={AppAssets?.icons?.editModel}
                  size={14}
                  color={currentTheme?.theme?.darkGrey}
                  isClickable={!isAIStreaming}
                  onPress={() => {
                    if (!isAIStreaming) {
                      setShowModelSelectionModal(true);
                    }
                  }}
                  wrapperStyle={{
                    opacity: isAIStreaming ? 0.5 : 1,
                  }}
                />
              )}
            </View>
          </Popable>

          {isLargeScreen() ? (
            (() => {
              const allTools = constructToolsListForAgentTools(
                primaryAgent,
                agentHosting,
                chatObject,
                canToggleTools,
                state?.agents
              );
              const staticTools =
                allTools?.filter((t) => !t?.isToggleable) || [];
              const toggleableTools =
                allTools?.filter((t) => t?.isToggleable) || [];

              return (
                <>
                  {/* Toggleable tools section (left) */}
                  {toggleableTools.length > 0 && (
                    <span
                      className="chat-tools-list-toggleable"
                      style={{ display: "flex", alignItems: "center", gap: 10 }}
                    >
                      <AppText
                        style={{
                          fontWeight: "bold",
                        }}
                        color={currentTheme?.theme?.darkGrey}
                        wrapperStyle={{
                          marginHorizontal: 10,
                        }}
                      >
                        |
                      </AppText>
                      {toggleableTools.map((u, index) => {
                        const isBeingToggled =
                          toolToggleInProgress === u?.property;

                        // Handle cycle toggle differently
                        let iconColor;
                        if (u?.isCycleToggle) {
                          // Cycle toggle uses state-specific colors
                          iconColor =
                            currentTheme?.theme?.[u?.cycleColor] ||
                            currentTheme?.theme?.darkGrey;
                        } else {
                          // Regular toggle uses orange when active, grey when inactive
                          iconColor = u?.isActive
                            ? currentTheme?.theme?.orange
                            : currentTheme?.theme?.darkGrey;
                        }

                        // Build tooltip content
                        let tooltipContent;
                        if (u?.isCycleToggle) {
                          tooltipContent = `${u?.description} - Click to cycle`;
                        } else {
                          tooltipContent = `${u?.description} (${
                            u?.isActive ? "enabled" : "disabled"
                          }) - Click to toggle`;
                        }

                        return (
                          <Popable
                            key={`toggle-tool-${index}-${u?.icon}`}
                            numberOfLines={3}
                            backgroundColor={
                              currentTheme?.theme?.tooltipBackgroundColor
                            }
                            style={[
                              {
                                top: 30,
                                zIndex: 20,
                                maxWidth: 400,
                              },
                            ]}
                            content={tooltipContent}
                            action="hover"
                            position="left"
                          >
                            <AppIcon
                              wrapperStyle={{
                                cursor: "pointer",
                                opacity: isBeingToggled ? 0.5 : 1,
                              }}
                              color={iconColor}
                              icon={AppAssets?.icons[u?.icon]}
                              size={16}
                              isClickable={!toolToggleInProgress}
                              onPress={
                                !toolToggleInProgress
                                  ? () => onToolToggle(u?.property, u?.isActive)
                                  : undefined
                              }
                            />
                          </Popable>
                        );
                      })}
                    </span>
                  )}

                  {/* Static tools section (right) */}
                  {staticTools.length > 0 && (
                    <span
                      className="chat-tools-list-static"
                      style={{ display: "flex", alignItems: "center", gap: 10 }}
                    >
                      <AppText
                        style={{
                          fontWeight: "bold",
                        }}
                        color={currentTheme?.theme?.darkGrey}
                        wrapperStyle={{
                          marginHorizontal: 10,
                        }}
                      >
                        |
                      </AppText>
                      {staticTools
                        .filter(
                          (u) =>
                            u?.icon === "notes" ||
                            u?.icon === "advanced" ||
                            u?.isPlannerAgent ||
                            u?.isPlannerAgentOverflow
                        )
                        .map((u, index) => {
                          if (u?.isPlannerAgent) {
                            return (
                              <Popable
                                key={`planner-agent-${index}-${u?.agentObject?.id}`}
                                numberOfLines={3}
                                backgroundColor={
                                  currentTheme?.theme?.tooltipBackgroundColor
                                }
                                style={[
                                  {
                                    top: 30,
                                    zIndex: 20,
                                    maxWidth: 400,
                                  },
                                ]}
                                content={u?.description}
                                action="hover"
                                position="left"
                              >
                                <AppIcon
                                  wrapperStyle={{
                                    cursor: "default",
                                  }}
                                  color={currentTheme?.theme?.darkGrey}
                                  icon={Utility.mapAgentTypeWithIcon(
                                    u?.agentObject,
                                    AppAssets
                                  )}
                                  size={16}
                                />
                              </Popable>
                            );
                          }

                          if (u?.isPlannerAgentOverflow) {
                            return (
                              <Popable
                                key={`planner-overflow-${index}`}
                                numberOfLines={3}
                                backgroundColor={
                                  currentTheme?.theme?.tooltipBackgroundColor
                                }
                                style={[
                                  {
                                    top: 30,
                                    zIndex: 20,
                                    maxWidth: 400,
                                  },
                                ]}
                                content={u?.description}
                                action="hover"
                                position="left"
                              >
                                <AppText
                                  style={{
                                    fontSize: 12,
                                    fontWeight: "bold",
                                  }}
                                  color={currentTheme?.theme?.darkGrey}
                                  preventTranslation
                                >
                                  +{u?.overflowCount}
                                </AppText>
                              </Popable>
                            );
                          }

                          return u?.icon === "notes" &&
                            primaryAgent?.allowedTopics?.length > 0 ? (
                            <Popable
                              key={`static-tool-${index}-${u?.icon}`}
                              numberOfLines={3}
                              backgroundColor={
                                currentTheme?.theme?.tooltipBackgroundColor
                              }
                              style={[
                                {
                                  top: 30,
                                  zIndex: 20,
                                  maxWidth: 400,
                                },
                              ]}
                              content={t("chats.ui.clickToSeeTopics")}
                              action="hover"
                              position="left"
                            >
                              <AppIcon
                                onPress={() => setIsTopicsModalOpen(true)}
                                isClickable
                                wrapperStyle={{
                                  cursor: "pointer",
                                }}
                                color={currentTheme?.theme?.darkGrey}
                                icon={AppAssets?.icons[u?.icon]}
                                size={16}
                              />
                            </Popable>
                          ) : u?.icon === "advanced" &&
                            primaryAgent?.agentFunctions?.length > 0 ? (
                            <Popable
                              key={`static-tool-${index}-${u?.icon}`}
                              numberOfLines={3}
                              backgroundColor={
                                currentTheme?.theme?.tooltipBackgroundColor
                              }
                              style={[
                                {
                                  top: 30,
                                  zIndex: 20,
                                  maxWidth: 400,
                                },
                              ]}
                              content={t("chats.ui.clickToSeeTools")}
                              action="hover"
                              position="left"
                            >
                              <AppIcon
                                isClickable
                                onPress={() => setIsToolsModalOpen(true)}
                                wrapperStyle={{
                                  cursor: "pointer",
                                }}
                                color={currentTheme?.theme?.darkGrey}
                                icon={AppAssets?.icons[u?.icon]}
                                size={16}
                              />
                            </Popable>
                          ) : null;
                        })}
                      {staticTools.filter(
                        (u) =>
                          u?.icon !== "notes" &&
                          u?.icon !== "advanced" &&
                          !u?.isPlannerAgent &&
                          !u?.isPlannerAgentOverflow
                      ).length > 0 && (
                        <Popable
                          numberOfLines={2}
                          backgroundColor={
                            currentTheme?.theme?.tooltipBackgroundColor
                          }
                          style={[
                            {
                              top: 30,
                              zIndex: 20,
                              maxWidth: 400,
                            },
                          ]}
                          content={t("chats.ui.clickToSeeOtherTools")}
                          action="hover"
                          position="left"
                        >
                          <AppIcon
                            isClickable
                            onPress={() => setIsOtherToolsModalOpen(true)}
                            wrapperStyle={{
                              cursor: "pointer",
                            }}
                            color={currentTheme?.theme?.darkGrey}
                            icon={AppAssets?.icons?.settings}
                            size={16}
                          />
                        </Popable>
                      )}
                    </span>
                  )}
                </>
              );
            })()
          ) : (
            <Popable
              numberOfLines={2}
              backgroundColor={currentTheme?.theme?.tooltipBackgroundColor}
              style={[
                {
                  top: 30,
                  zIndex: 20,
                  maxWidth: 300,
                },
              ]}
              content={t("chats.ui.viewToolsAndSettings")}
              action="hover"
              position="left"
            >
              <AppIcon
                wrapperStyle={{
                  marginLeft: 10,
                  cursor: "pointer",
                }}
                color={currentTheme?.theme?.darkGrey}
                icon={AppAssets?.icons?.menu}
                size={18}
                isClickable
                onPress={() => setIsCollapsedToolsModalOpen(true)}
                isHoverable
              />
            </Popable>
          )}
        </View>
        {isLargeScreen() && (
          <>
            <AppText
              style={{
                fontWeight: "bold",
              }}
              color={currentTheme?.theme?.darkGrey}
              wrapperStyle={{
                marginHorizontal: 10,
              }}
            >
              |
            </AppText>
            {primaryAgent?.mode == "planner" &&
              isAIStreaming &&
              totalSteps > 0 &&
              activeStep > 0 && (
                <Popable
                  numberOfLines={2}
                  backgroundColor={currentTheme?.theme?.tooltipBackgroundColor}
                  style={[
                    {
                      top: 30,
                      zIndex: 20,
                      maxWidth: 300,
                    },
                  ]}
                  content={`Running step ${activeStep || 1}`}
                  action="hover"
                  position="left"
                >
                  <AppText
                    style={{
                      fontWeight: "bold",
                    }}
                    color={currentTheme?.theme?.primary}
                    preventTranslation
                    rightIcon={AppAssets?.icons?.plannerStep}
                    iconSize={16}
                    wrapperStyle={{
                      marginRight: 20,
                    }}
                  >
                    {" "}
                    {activeStep || 1}
                  </AppText>
                </Popable>
              )}
            {planArtifacts?.length > 0 && (
              <Popable
                numberOfLines={2}
                backgroundColor={currentTheme?.theme?.tooltipBackgroundColor}
                style={[
                  {
                    top: 30,
                    zIndex: 20,
                    maxWidth: 300,
                  },
                ]}
                content={t("chats.ui.showArtifactsTooltip")}
                action="hover"
                position="left"
              >
                <AppIcon
                  wrapperStyle={{
                    marginRight: 10,
                  }}
                  isClickable
                  onPress={() => {
                    setShowArtifactsModal(true);
                  }}
                  style={{
                    fontSize: currentTheme?.theme?.extra_small_font_size,
                    fontWeight: "bold",
                    color: currentTheme?.theme?.orange,
                    marginLeft: 5,
                    textDecorationColor: currentTheme?.theme?.orange,
                  }}
                  icon={AppAssets?.icons?.artifacts}
                  color={currentTheme?.theme?.orange}
                  isHoverable
                />
              </Popable>
            )}
            {fixedDocumentId && (
              <Popable
                numberOfLines={2}
                backgroundColor={currentTheme?.theme?.tooltipBackgroundColor}
                style={[
                  {
                    top: 30,
                    zIndex: 20,
                    maxWidth: 300,
                  },
                ]}
                content={
                  documents.find((u) => u?.id == fixedDocumentId)?.title
                    ? "Access to document"
                    : "No access to document"
                }
                action="hover"
                position="left"
              >
                <AppIcon
                  icon={
                    documents.find((u) => u?.id == fixedDocumentId)?.title
                      ? AppAssets.icons?.notes
                      : AppAssets?.icons.noAccess
                  }
                  isHoverable
                  color={currentTheme?.theme?.darkGrey}
                  style={{
                    fontSize: currentTheme?.theme?.extra_small_font_size,
                    fontWeight: "bold",
                    color: currentTheme?.theme?.darkGrey,
                    marginLeft: 5,
                    textDecorationColor: currentTheme?.theme?.darkGrey,
                    marginRight: 15,
                  }}
                />
              </Popable>
            )}

            {/* Export Plan - only visible in planner mode */}
            {showPlannerSteps && (
              <Popable
                numberOfLines={2}
                backgroundColor={currentTheme?.theme?.tooltipBackgroundColor}
                style={[
                  {
                    top: 30,
                    zIndex: 20,
                    maxWidth: 300,
                  },
                ]}
                content={t("chats.placeholders.copyPlan")}
                action="hover"
                position="left"
              >
                <AppIcon
                  isClickable
                  onPress={exportPlanToClipboard}
                  color={currentTheme?.theme?.primary}
                  icon={AppAssets?.icons?.plan}
                  isHoverable
                  wrapperStyle={{
                    marginRight: 10,
                  }}
                />
              </Popable>
            )}

            {isUserAIEngineer() && !isTfMainAgent && (
              <Popable
                numberOfLines={2}
                backgroundColor={currentTheme?.theme?.tooltipBackgroundColor}
                style={[
                  {
                    top: 30,
                    zIndex: 20,
                    maxWidth: 300,
                  },
                ]}
                content={
                  typeof chatObject?.metadata === "string"
                    ? JSON.parse(chatObject.metadata)?.markedForTraining
                      ? t("chats.ui.unmarkForTraining")
                      : t("chats.ui.markForTraining")
                    : chatObject?.metadata?.markedForTraining
                    ? t("chats.ui.unmarkForTraining")
                    : t("chats.ui.markForTraining")
                }
                action="hover"
                position="left"
              >
                <AppIcon
                  isClickable
                  onPress={onMarkForTraining}
                  color={
                    typeof chatObject?.metadata === "string"
                      ? JSON.parse(chatObject.metadata)?.markedForTraining
                        ? currentTheme?.theme?.orange
                        : currentTheme?.theme?.primary
                      : chatObject?.metadata?.markedForTraining
                      ? currentTheme?.theme?.orange
                      : currentTheme?.theme?.primary
                  }
                  icon={AppAssets?.icons?.trainAI}
                  isHoverable
                  wrapperStyle={{
                    marginRight: 10,
                  }}
                />
              </Popable>
            )}
            {canViewUoIUsage && (
              <Popable
                numberOfLines={2}
                backgroundColor={currentTheme?.theme?.tooltipBackgroundColor}
                style={[
                  {
                    top: 30,
                    zIndex: 20,
                    maxWidth: 300,
                  },
                ]}
                content={t("chats.ui.uoiUsage")}
                action="hover"
                position="left"
              >
                <AppIcon
                  isClickable
                  onPress={() => setShowUoIUsageModal(true)}
                  color={currentTheme?.theme?.primary}
                  icon={AppAssets?.icons?.consumedUoI}
                  isHoverable
                  wrapperStyle={{
                    marginRight: 10,
                  }}
                />
              </Popable>
            )}
            <Popable
              numberOfLines={2}
              backgroundColor={currentTheme?.theme?.tooltipBackgroundColor}
              style={[
                {
                  top: 30,
                  zIndex: 20,
                  maxWidth: 300,
                },
              ]}
              content={
                messages?.length > 0 ? t("chats.export") : t("chats.empty")
              }
              action="hover"
              position="left"
            >
              <AppIcon
                isClickable={messages?.length > 0}
                onPress={createMessagesExportFile}
                color={
                  messages?.length > 0
                    ? currentTheme?.theme?.primary
                    : currentTheme?.theme?.mediumGrey
                }
                icon={AppAssets?.icons?.export}
                isHoverable
              />
            </Popable>

            {/* Queue icon - shows pending messages when agenticRAG or useInterleavedReasoning is active */}
            {(() => {
              if (pendingUserMessages?.length > 0) {
                return (
                  <Popable
                    numberOfLines={2}
                    backgroundColor={
                      currentTheme?.theme?.tooltipBackgroundColor
                    }
                    style={[
                      {
                        top: 30,
                        zIndex: 20,
                        maxWidth: 300,
                      },
                    ]}
                    content={`${pendingUserMessages?.length} message${
                      pendingUserMessages?.length > 1 ? "s" : ""
                    } in queue`}
                    action="hover"
                    position="left"
                  >
                    <AppIcon
                      isClickable
                      onPress={() => setIsQueueModalOpen(true)}
                      wrapperStyle={{
                        marginLeft: 10,
                      }}
                      color={currentTheme?.theme?.orange}
                      icon={AppAssets?.icons?.queue}
                      isHoverable
                    />
                  </Popable>
                );
              }
              return null;
            })()}
          </>
        )}
      </View>
    );
  }, [
    t,
    planningExecution,
    onSelectedTopicsChange,
    JSON.stringify(selectedTopics),
    JSON.stringify(topics),
    JSON.stringify(primaryAgent),
    JSON.stringify(messages),
    pendingUserMessages,
    isLargeScreen(),
    isMobileScreen(),
    allowStop,
    isAIStreaming,
    totalSteps,
    activeStep,
    agentHosting,
    JSON.stringify(chatObject),
    canChangeModel,
    isAwaitingUserInput,
    showPlannerSteps,
  ]);

  const chatNameComponent = useMemo(() => {
    const language =
      chatObject?.language && chatObject?.language !== "def"
        ? ` - ${
            state?.languagesConfig?.find((u) => u?.id == chatObject?.language)
              ?.name
          }
          `
        : "";
    return (
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          marginRight: 20,
          flex: "auto",
          flexWrap: "wrap",
          width: "-webkit-fill-available",
          marginTop: -2,
        }}
      >
        <AppText
          style={{
            marginRight: 10,
            marginLeft: isCollapsed ? 22 : 10,
            width: "-webkit-fill-available",
            overflow: "hidden",
            whiteSpace: "nowrap",
            textOverflow: "ellipsis",
            flex: 0.7,
            fontSize: currentTheme?.theme?.large_font_size,
          }}
          showTypingEffect
          typingSpeed={10}
          wrapperStyle={{
            marginRight: 20,
            flex: 1,
          }}
          fontWeight="bold"
        >
          {!chatObject?.name || chatObject?.name === "New Chat"
            ? t("chats.newChatName")
            : chatObject?.name}
        </AppText>
      </View>
    );
  }, [
    JSON.stringify(chatObject),
    state?.languagesConfig,
    isLargeScreen(),
    messages?.length,
    getActiveWorkspace()?.subscriptionType,
    isCollapsed,
  ]);
  // Filter messages for chat when steps panel is shown
  // Chat shows: user messages + planner responses to user
  // Steps panel shows: agent execution (planner-to-agent, agent responses)
  const chatMessages = useMemo(() => {
    if (!showPlannerSteps) return messages;
    return messages?.filter(shouldShowInChat) || [];
  }, [messages, showPlannerSteps]);

  const coreChatView = useMemo(() => {
    return (
      ((primaryAgent?.icon && agentImage) ||
        !primaryAgent?.icon ||
        !isConnectedToCloud()) && (
        <View
          style={{
            flex: 1,
            flexDirection:
              ((documentId && isCanvasOpen) || showPlannerSteps) &&
              "row-reverse",
          }}
        >
          {/* PlannerStepsViewer (dev-only) */}
          {showPlannerSteps && (
            <PlannerStepsViewer
              messages={messages}
              activeStep={activeStep}
              activeSteps={activeSteps}
              totalSteps={totalSteps}
              planningExecution={planningExecution}
              primaryAgent={primaryAgent}
              workspaceid={workspaceid}
              S3downloadUrlRequest={S3downloadUrlRequest}
              getAgent={getAgent}
              height={height}
              stepsStatusFromArtifacts={stepsStatusFromArtifacts}
              onToggleExpand={(isExpanded) =>
                setPlannerStepsExpanded(isExpanded)
              }
            />
          )}
          {documentId && isCanvasOpen && (
            <EditorWrapper
              key="canvas-editor"
              {..._editorStyles}
              isToolBarVisible
              workspaceid={getActiveWorkspace()?.id}
              editMode={true}
              isPDF={false}
              isPlaceHolderEnabled
              allowImages
              placeholder={t("chats.ui.editorPlaceholder")}
              onSocketConnectionChange={handleSocketConnectionChange}
              onError={handleEditorError}
              value={value}
              setValue={setValue}
              documentId={documentId}
              isInactive={isInactive}
              words={wordsCount}
              characters={charactersCount}
              wordsLabel={t("editor.remainingWords")}
              charactersLabel={t("editor.remainingCharacters")}
              onRecordClick={handleOnRecorderClick}
              onSelectionQuantumEditor={onSelectionQuantumEditor}
              showSelectionCounter
              allowTranscription={true}
              setEditor={setEditor}
              allowExport
              maxWords={30000}
              maxCharacters={180000}
              initialValue={[
                { type: "title", children: [{ text: "New Canvas" }] },
                {
                  type: "paragraph",
                  children: [
                    {
                      text: "",
                    },
                  ],
                },
              ]}
            />
          )}
          <AppQuantumChat
            key="chat-main"
            cursor={cursor}
            disableEditingFeedback={!canInteractWithChat}
            allowStop={allowStop}
            agentDefaultIcon={agentImage || require("../../assets/favicon.png")}
            sendOnEnter
            showLoading={false}
            onCopy={handleCopy}
            renderMarkdown
            loadEarlier={allMessagesLoaded}
            messages={chatMessages}
            setMessages={setMessages}
            showAgentName={forceAgentsName || primaryAgent?.showAgentName}
            coder={true}
            noDelay={true}
            requiresApproval={
              planningExecution === "planning_requires_approval" ||
              planningExecution === "step_requires_approval"
            }
            sendApprovalMessage={(msg, id) => {
              onSendEvent(msg);
              updatePlanApprovalState(id);
            }}
            onSendEvent={onSendEvent}
            handleShowSourcesDetails={handleShowSourcesDetails}
            user={{
              id: user?.id,
              name: user?.username,
            }}
            chatStyle={{
              height:
                (isHomeMode
                  ? primaryAgent?.mode === "voice"
                    ? height - 120
                    : height - 90
                  : height - 90) - (builderMode ? 70 : 0),
              width:
                documentId && isCanvasOpen
                  ? 425
                  : showPlannerSteps && plannerStepsExpanded
                  ? 425
                  : "100%",
            }}
            inverted={false}
            showUserAvatar
            messagesContainerStyle={{
              marginTop: 0,
              paddingLeft: 10,
            }}
            hideReasoning={isTfMainAgent || primaryAgent?.hideReasoning}
            showCodeInExecution={primaryAgent?.showCodeInExecution != false}
            chatid={chatObject?.id}
            allowFeedback={
              primaryAgent?.allowFeedback &&
              chatObject?.visibility != "incognito"
            }
            handleFeedback={handleFeedback}
            listenToAudio={listenToAudio}
            maxComposerHeight={60}
            composerHeight={60}
            sortOrder="asc"
            placeholderDisabled="The agent is responding..."
            isComposerDisabled={isAIStreaming}
            allowUserToTypeWhileGeneratingResponse={!isAIReplyingOtherUsers}
            waitingForFirstResponse={waitingForFirstResponse}
            waitingForFirstChunk={waitingForFirstChunk}
            onCancel={onCancelGeneration}
            inputText={inputText}
            setInputText={setInputText}
            renderWithVanillaInput={false}
            onLoadEarlier={onLoadEarlier}
            S3downloadUrlRequest={S3downloadUrlRequest}
            workspaceid={workspaceid}
            showDuration={primaryAgent?.showTimeForResponse}
            showRoutedModel={primaryAgent?.showRoutedModel}
            showSplash={primaryAgent?.showSplashWhenEmpty}
            splashTitle={isTfMainAgent ? "" : primaryAgent?.messageOnLaunch}
            showLanguage={primaryAgent?.showDetectedLanguage}
            showProcessingMetadata={true}
            rlhf={
              chatObject?.visibility === "private" ||
              chatObject?.visibility === "widget"
            }
            canvasID={chatObject?.canvasID}
            plannerMode={primaryAgent?.mode == "planner"}
            voiceMode={primaryAgent?.mode === "voice"}
            isLastMessageEmpty={isLastMessageEmpty}
            customChartingConfig={getActiveWorkspace()?.customChartingConfig}
            isChatEmpty={chatMessages?.length == 0}
            onImageSelected={onImageSelected}
            selectedImageKey={selectedImageKey}
            isSpeechGettingProcessed={isSpeechGettingProcessed}
            updateTextForSpeech={updateTextForSpeech}
            showTextToSpeech={primaryAgent?.allowAudiosGeneration}
            textToDisplayWhileLoading=""
            agenticAI={
              primaryAgent?.agenticRAG ||
              primaryAgent?.useInterleavedReasoning ||
              primaryAgent?.mode === "planner"
            }
            useInterleavedReasoning={primaryAgent?.useInterleavedReasoning}
            agents={agents}
            onCitationClick={(source, citationNumber) => {
              setSelectedCitation({ source, citationNumber });
              setShowCitationModal(true);
            }}
            onForkConversation={handleForkConversation}
          >
            <>
              <ChatWebInput
                createMessagesExportFile={createMessagesExportFile}
                isExportVisible={messages?.length > 0}
                isPlaying={isPlaying}
                isSpeechGettingProcessed={isSpeechGettingProcessed}
                text={inputText}
                setText={setInputText}
                documentId={documentId}
                isStreaming={isAIStreaming}
                handleCanvasCreation={handleCanvasCreation}
                isCanvasOpen={isCanvasOpen}
                onToggleCanvas={() => setIsCanvasOpen((v) => !v)}
                agenticTooling={isTfMainAgent || primaryAgent?.agenticRAG}
                isPlannerAcceptingFeedback={isFeedbackOnPlanAllowed}
                isAgenticRAGAcceptingFeedback={isAgenticRAGAcceptingFeedback}
                onCancel={onCancelGeneration}
                onSend={onSendEvent}
                stopAudio={stopAudio}
                allowStop={allowStop}
                agentMode={primaryAgent?.mode}
                isLastMessageEmpty={isLastMessageEmpty}
                showWidgetChatsHistory={showWidgetChatsHistory}
                canInteractWithChat={canInteractWithChat}
                isAwaitingUserInput={isAwaitingUserInput}
                messages={chatMessages}
                isChatEmpty={chatMessages?.length == 0}
                primaryAgentId={
                  primaryAgent?.id || (isTfMainAgent ? "tf_main" : null)
                }
                agents={agents}
                onAgentSelected={onAgentSelected}
                onSendWithAgent={onSendWithAgent}
                forcePlannerStop={chatObject?.forcePlannerStop}
                handedOffToHuman={chatObject?.handedOffToHuman}
                quickQuestions={primaryAgent?.quickQuestions
                  ?.split(";")
                  ?.map((u) => u?.trim()?.replace(/\s+/g, " "))
                  ?.filter((u) => u != "")}
                allowImagesUpload={
                  (isTfMainAgent || primaryAgent?.allowImagesUpload) &&
                  agentHosting?.type != "ollama"
                }
                allowAudiosUpload={
                  (isTfMainAgent || primaryAgent?.allowAudiosUpload) &&
                  agentHosting?.type != "ollama"
                }
                allowVideosUpload={
                  (isTfMainAgent || primaryAgent?.allowVideosUpload) &&
                  agentHosting?.type != "ollama"
                }
                allowAudiosGeneration={
                  (isTfMainAgent || primaryAgent?.allowAudiosGeneration) &&
                  agentHosting?.type != "ollama"
                }
                isAutoReplyMode={
                  primaryAgent?.mode === "voice" &&
                  agentHosting?.type != "ollama"
                }
                allowFileUpload={
                  (isTfMainAgent ||
                    primaryAgent?.hasCode ||
                    primaryAgent?.allowDocsUpload) &&
                  agentHosting?.type != "ollama"
                }
                allowCodeUpload={
                  primaryAgent?.allowDocsUpload &&
                  primaryAgent?.mode == "coder" &&
                  agentHosting?.type != "ollama"
                }
                allowMiscFileTypes={
                  ((primaryAgent?.hasCode && primaryAgent?.mode == "coder") ||
                    (primaryAgent?.mode == "planner" &&
                      primaryAgent?.allowDocsUpload)) &&
                  agentHosting?.type != "ollama"
                }
                onImageUploaded={onImageUploaded}
                onAudioUploaded={onAudioUploaded}
                onFileUploaded={onFileUploaded}
                fileUrl={fileUrl}
                setFileUrl={setFileUrl}
                imageUrl={imageUrl}
                setImageUrl={setImageUrl}
                audioUrl={audioUrl}
                setAudioUrl={setAudioUrl}
                onVideoUploaded={onVideoUploaded}
                videoUrl={videoUrl}
                setVideoUrl={setVideoUrl}
                placeholder={
                  selectedImageKey
                    ? "How would you like to refine the image generated?"
                    : isTfMainAgent
                    ? t("chats.ui.intelligenceQuote")
                    : primaryAgent?.placeholderInputMessage ||
                      t("chats.inputPlaceholder")
                }
                onFilesUploaded={onFilesUploaded}
                fileUrls={fileUrls}
                setFileUrls={setFileUrls}
                isPlanner={primaryAgent?.mode == "planner"}
                planningExecution={planningExecution}
                imageUrls={imageUrls}
                audioUrls={audioUrls}
                // LiveKit Voice Mode props
                workspaceId={workspaceid}
                apiKey={getActiveWorkspace()?.workspaceToken}
                chatId={chatId}
                // Live session (mixed text/voice) props
                liveSessionId={liveSessionId}
                onVoiceSessionEnded={onVoiceSessionEnded}
                // Voice settings/controls come from LiveVoiceContext (hoisted provider)
                // Plan mode props
                planMode={planMode}
                planFile={planFile}
                planStatus={planStatus}
                onPlanModeToggle={handlePlanModeToggle}
                onShowPlan={handleShowPlan}
                agenticRAG={primaryAgent?.agenticRAG}
                useInterleavedReasoning={primaryAgent?.useInterleavedReasoning}
                // Agent capabilities for tool filtering
                agentCharting={primaryAgent?.charting}
                agentAllowInternetSearch={primaryAgent?.allowInternetSearch}
                agentAllowDeepInternetSearch={
                  primaryAgent?.allowDeepInternetSearch
                }
                agentAllowImagesGeneration={primaryAgent?.allowImagesGeneration}
                agentAllowVideosGeneration={primaryAgent?.allowVideosGeneration}
                agentHasCode={primaryAgent?.hasCode}
                agentHasImages={primaryAgent?.hasImages}
                agentEmailAsSender={primaryAgent?.emailAsSender}
                agentPhoneNumberAsSender={primaryAgent?.phoneNumberAsSender}
                agentWhatsappNumberAsSender={
                  primaryAgent?.whatsappNumberAsSender
                }
                agentAllowAskQuestion={primaryAgent?.allowAskQuestion}
                agentDesktopVM={primaryAgent?.agentDesktopVM}
                // Custom agent functions for tool selection dropdown (resolved from IDs to full objects)
                availableFunctions={resolvedAgentFunctions?.sort((a, b) =>
                  a.label.localeCompare(b.label)
                )}
                // Folder binding props
                allowFolderBinding={
                  primaryAgent?.mode === "retriever" &&
                  (primaryAgent?.agenticRAG == true ||
                    primaryAgent?.useInterleavedReasoning) &&
                  !!window?.electronAPI
                }
                onFolderBound={onFolderBound}
                onFolderUnbound={onFolderUnbound}
                isHomeMode={isHomeMode}
                boundFolder={
                  chatObject?.metadata?.folderBinding
                    ? {
                        path: chatObject.metadata.folderBinding.path,
                        name: chatObject.metadata.folderBinding.name,
                      }
                    : null
                }
                recentFolders={recentFolders}
                onUpdateRecentFolders={onUpdateRecentFolders}
              />
            </>
          </AppQuantumChat>
        </View>
      )
    );
  });
  const _treeView = useMemo(() => {
    const headerComponents = [
      <ToothFairySupportButton />,
      <Popable
        numberOfLines={2}
        backgroundColor={currentTheme?.theme?.tooltipBackgroundColor}
        style={[
          {
            top: 30,
            left: 20,
            zIndex: 20,
            maxWidth: 300,
          },
        ]}
        content={t("chats.ui.newChat")}
        action="hover"
        position="right"
      >
        <div className="start_chat">
          <AppIcon
            icon={AppAssets.icons.chatIcon}
            color={currentTheme?.theme?.darkGrey}
            isHoverable
            isClickable
            onPress={() => {
              if (creationOfNewChatInProgress) return;
              if (isHomeMode) {
                handleParentActionClick(homeAgentId);
              } else {
                setDialogActionOnTree("NEW_CHAT");
              }
            }}
            wrapperStyle={{
              marginRight: 10,
              marginLeft: 0,
              marginTop: 2,
            }}
          />
        </div>
      </Popable>,
      !isHomeMode && isConnectedToCloud() && isUserContentAdmin() && (
        <Popable
          numberOfLines={2}
          backgroundColor={currentTheme?.theme?.tooltipBackgroundColor}
          style={[
            {
              top: 30,
              zIndex: 20,
              maxWidth: 400,
            },
          ]}
          content={t("chats.ui.goToHub")}
          action="hover"
          position="bottom"
        >
          <div className="upload_docs">
            <AppIcon
              icon={AppAssets.icons.notes}
              color={currentTheme?.theme?.darkGrey}
              size={20}
              isHoverable
              isClickable
              onPress={() => {
                history.push(
                  `/workspaces/${getActiveWorkspace()?.id}/mrc_contents`
                );
              }}
            />
          </div>
        </Popable>
      ),

      !isHomeMode && isConnectedToCloud() && isUserAIEngineer() && (
        <View
          style={{
            flexDirection: "row",
          }}
        >
          <AppText
            style={{
              fontWeight: "bold",
            }}
            color={currentTheme?.theme?.darkGrey}
            wrapperStyle={{
              marginHorizontal: 10,
            }}
          >
            |
          </AppText>
          <Popable
            numberOfLines={2}
            backgroundColor={currentTheme?.theme?.tooltipBackgroundColor}
            style={[
              {
                top: 30,
                zIndex: 20,
                maxWidth: 400,
              },
            ]}
            content={t("chats.ui.hireAgent")}
            action="hover"
            position="bottom"
          >
            <AppIcon
              icon={AppAssets.icons.hire}
              color={currentTheme?.theme?.darkGrey}
              isHoverable
              isClickable
              onPress={() => {
                history.push(
                  `/workspaces/${
                    getActiveWorkspace()?.id
                  }/settings/roles_settings/hire`
                );
              }}
            />
          </Popable>
        </View>
      ),
    ].filter(Boolean);

    return (isTreeDataLoading || _coreTreeViewData === null) && !isCollapsed ? (
      <LoadingPlaceholder
        style={{
          padding: 10,
          minWidth: 330,
        }}
        visible
        caption={t("chats.ui.loading")}
      />
    ) : (
      <AppTreeView
        headerComponents={headerComponents}
        indent={20}
        allowParentEdit={false}
        allowParentAction={true}
        onParentActionClick={handleParentActionClick}
        onToggleFavorite={onToggleFavorite}
        requireNormalisation={false}
        showCollapseTree={false}
        title={" "}
        treemode="CHATS"
        preventSortingByName={true}
        filterPlaceholder={t("chats.ui.filterAgentsAndChats")}
        foldersData={_coreTreeViewData ? [..._coreTreeViewData] : []}
        selectedNode={selectedNode}
        handleClickOnNode={handleClickOnNode}
        deleteNode={deleteNode}
        setDialogActionOnTree={setDialogActionOnTree}
        isEmpty={_coreTreeViewData?.length === 0}
        onDrop={onDrop}
        initialOpen={false}
        minWidth={330}
        isCollapsed={isCollapsed}
        setIsCollapsed={setIsCollapsed}
        showIconsOnSelection={true}
        onMove={onMove}
        deleteAction="DELETE_NODE"
        editLeafAction="EDIT_CHAT"
        editLeafParentAction="EDIT_CHAT"
        heightOffset={isMoreToLoad ? 145 : 125}
        disableTFDrag={true}
      />
    );
  }, [
    t,
    _coreTreeViewData?.map((u) => {
      return {
        id: u?.id,
        name: u?.name,
        description: u?.description || t("chats.ui.noDescriptionAvailable"),
      };
    }),
    isTreeDataLoading,
    isCollapsed,
    creationOfNewChatInProgress,
    getActiveWorkspace()?.id,
    currentTheme?.theme?.darkGrey,
    isUserAIEngineer(),
    isUserContentAdmin(),
  ]);
  const _actionsChoice = () => {
    return (
      <ToothFairyResponsiveOptionsModal
        isModalOpen={isModalOptionsOpen}
        setIsModalOpen={setIsModalOptionsOpen}
        modalTitle={t("chats.ui.actions")}
        height={300}
      >
        <ToothFairyClickableOption
          leftIcon={Utility.mapAgentTypeWithIcon(primaryAgent, AppAssets)}
          onPress={() => {
            if (creationOfNewChatInProgress) return;
            setIsModalOptionsOpen(false);
            if (isHomeMode) {
              handleParentActionClick(homeAgentId);
            } else {
              setDialogActionOnTree("NEW_CHAT");
            }
          }}
        >
          {t("chats.ui.newChat")}
        </ToothFairyClickableOption>
      </ToothFairyResponsiveOptionsModal>
    );
  };
  const _coreHeaderComponent = useMemo(() => {
    return (
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          marginTop: 5,
          justifyContent: "space-between",
          width: "100%",
        }}
      >
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
          }}
        >
          <ToothFairySupportButton />

          <></>
        </View>
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            marginRight: 10,
          }}
        ></View>
      </View>
    );
  }, [
    setDialogActionOnTree,
    isLargeScreen(),
    JSON.stringify(state?.agents),
    JSON.stringify(messages),
    getActiveWorkspace()?.id,
    planArtifacts?.length,
  ]);
  return (
    <>
      <AppBaseScreen
        isScrollable={true}
        contentContainerStyle={{
          overflow: "auto",
          flex: 1,
        }}
      >
        <AppJoyRide
          handleCallback={(e) => {
            if (user?.userHelperPreferences?.knowledgeHubVisited) {
              if (e?.action == "next" && e?.index == 0) {
                completeTourIfEligible(TOUR_FLAGS.chatsTourCompleted);
                formRef?.current?.resetForm();
                setTreeViewActionContext("NEW_CHAT");
                setShowModal(true);
              }
            } else if (!user?.userHelperPreferences?.knowledgeHubVisited)
              if (e?.action == "next" && e?.index == 0) {
                history.push(
                  `/workspaces/${getActiveWorkspace()?.id}/mrc_contents`
                );
                updateUserHelperPreferences(user, {
                  knowledgeHubVisited: true,
                });
              }
          }}
          run={
            isPlatformSupported &&
            chatTourReady &&
            (needsTour(TOUR_FLAGS.knowledgeHubVisited) ||
              needsTour(TOUR_FLAGS.chatsTourCompleted))
          }
          steps={
            user?.userHelperPreferences?.knowledgeHubVisited
              ? steps_second_interaction
              : steps_first_interaction
          }
          spotlightClicks={true}
        />
        <View
          style={{
            paddingLeft: 5,
            paddingRight: 20,
            paddingTop: 5,
            paddingBottom: 0,
            minWidth: minAppWidth(),
            flex: 1,
          }}
        >
          <View
            style={{
              flexDirection: "row",
              justifyContent: "space-between",
              flexWrap: "wrap",
              maxWidth: isMobileScreen() && "100%",
            }}
          >
            {_coreHeaderComponent}
          </View>
          <View
            style={{
              flexDirection: "row",
              flex: 1,
              zIndex: -1,
            }}
          >
            <View
              style={{
                flexDirection: "column",
                height: "100%",
                position: isCollapsed && "absolute",
              }}
            >
              {isConnectedToCloud() && _treeView}
              {isMoreToLoad &&
                !isTreeDataLoading &&
                _coreTreeViewData?.length > 0 &&
                !isCollapsed && (
                  <AppText
                    wrapperStyle={{
                      alignSelf: "center",
                      marginTop: 5,
                    }}
                    isClickable
                    style={{
                      color: currentTheme?.theme?.primary,
                    }}
                    fontWeight="bold"
                    onPress={async () => {
                      setIsChatHistoryLoaded(false);
                      await loadChats(showWidgetChatsHistory);
                      setIsChatHistoryLoaded(true);
                    }}
                  >
                    common.loadMore
                  </AppText>
                )}
            </View>

            {!isConnectedToCloud() ? (
              !isElectronRunning() ? (
                <>{coreChatView}</>
              ) : (
                <AppPlaceholderView
                  accessibilityLabel="chat-placeholder"
                  wrapperStyle={{
                    width: 400,
                    height: 220,
                    marginVertical: "auto",
                    marginHorizontal: "auto",
                  }}
                  size={30}
                  icon={AppAssets.icons.orbit}
                  mainCaption={"ChatSubscriptionErrorMainCaption"}
                  subCaption={"ChatSubscriptionErrorSubCaption"}
                />
              )
            ) : (isLoading || !chatObject) &&
              chatId &&
              isConnectedToCloud() ? (
              <LoadingPlaceholder
                style={{
                  padding: 10,
                }}
                visible
                caption={t("chats.ui.loadingChat")}
              />
            ) : chatId && hasAccessToChat ? (
              <View
                style={{ flex: 1, minWidth: minAppWidth() - 30, zIndex: -1 }}
              >
                <View
                  style={{
                    zIndex: 3,
                    flexDirection: "row",
                    borderBottomColor: currentTheme?.theme?.darkGrey,
                    borderBottomWidth: 0.5,
                    marginBottom: 5,
                  }}
                >
                  {chatNameComponent}
                  <View
                    style={{
                      flexDirection: "row",
                      justifyContent: "space-between",
                      alignItems: "center",
                      paddingVertical: 0,
                      zIndex: 3,
                      // minHeight: 78,
                      flexWrap: "wrap",
                      paddingBottom: isMobileScreen() && 10,
                    }}
                  >
                    {topicSelectionComponent}
                  </View>
                </View>
                {coreChatView}
              </View>
            ) : isHomeMode && !chatId ? (
              <View
                style={{ flex: 1, minWidth: minAppWidth() - 30, zIndex: -1 }}
              >
                {coreChatView}
              </View>
            ) : (
              <AppPlaceholderView
                accessibilityLabel="chat-placeholder"
                wrapperStyle={{
                  width: 325,
                  height: 220,
                  marginVertical: "auto",
                  marginHorizontal: "auto",
                }}
                size={30}
                icon={AppAssets.icons.orbit}
                mainCaption={
                  chatId
                    ? t("chats.doesNotExistCaption")
                    : t("chats.selectChatCaption")
                }
                subCaption={
                  chatId
                    ? t("chats.doesNotExistSubCaption")
                    : t("chats.selectChatSubCaption")
                }
              />
            )}
          </View>
        </View>
        {_coreModalView}
        {_coreSourcesModelView}
        {_plannerModalView}
        {_artifactsModalView}
        {_citationModalView}
        {_topicsModalView}
        {_toolsModalView}
        {_otherToolsModalView}
        {_modelSelectionModalView}
        {_queueModalView}
        {_collapsedToolsModalView}
        {_actionsChoice()}
        <UoIUsageModal
          visible={showUoIUsageModal}
          setVisible={setShowUoIUsageModal}
          chatId={chatId}
          messages={messages}
        />
      </AppBaseScreen>
    </>
  );
};

export default ChatsScreenView;
